import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import ReactDOM from 'react-dom';
import { useDocumentTitle } from '@mantine/hooks';
import { HeaderMenu } from "../../components/HeaderMenu/HeaderMenu";
import { Skeleton, Container, Card, Group, Text, Select, Button, Space, Grid, Table, ActionIcon, Box, MultiSelect, Anchor, Menu, Popover, TextInput, Checkbox, ScrollArea, Tooltip, useMantineTheme, Alert, Modal, Switch, NumberInput, Badge, Collapse, Flex, Chip } from '@mantine/core';
import { IconHelp, IconSettings, IconRefresh, IconChevronLeft, IconChevronRight, IconChevronDown, IconX, IconInfoCircle, IconTarget, IconBulb, IconArrowsHorizontal, IconArrowsRightLeft } from '@tabler/icons-react';
import './ReviewingTest.css';

import CampaignReport from "../../api/CampaignReport";
import GeneralComponentApi from "../../api/GeneralComponentApi";
import type ExcelJS from 'exceljs';
import { Chart } from "react-google-charts";
import { notifications } from '@mantine/notifications';
import { SelectTimePicker, VisiLoader, SelectTooltip } from "../../components/GeneralComponent/GeneralComponent";
import PermissionWrapper from '../../PermissionWrapper';
import TestsApi from "../../api/TestsApi";
import { PauseResume } from '../../components/ReviewingTest/PauseResume';
import { TestInfo } from '../../components/ReviewingTest/TestInfo';
import { DiscardTest } from '../../components/ReviewingTest/DiscardTest';
import { AudienceSize } from '../../components/ReviewingTest/AudienceSize';
import { UniversalSmartCodeModal } from '../../components/Settings/ManageYourDomains/UniversalSmartCodeModal';
import { useAuth } from '../../AuthContext';
import { TestMode } from '../../components/ReviewingTest/TestMode';
import { TestNotes } from '../../components/ReviewingTest/TestNotes';
import { Logs } from '../../components/ReviewingTest/Logs';
import { GoogleAnalytics } from '../../components/ReviewingTest/GoogleAnalytics';
import { TrafficControl } from '../../components/ReviewingTest/TrafficControl';
import { Attribution } from '../../components/ReviewingTest/Attribution';
import { AttributionWindow } from '../../components/ReviewingTest/AttributionWindow';
import { ResetStats } from '../../components/ReviewingTest/ResetStats';
import { BehavioralFilter } from '../../components/ReviewingTest/BehavioralFilter';
import { SetWinner } from '../../components/ReviewingTest/SetWinner';
import { CustomTitle } from '../../components/ReviewingTest/CustomTitle';
import { PauseVariations } from '../../components/ReviewingTest/PauseVariations';
import { Variables } from '../../components/ReviewingTest/Variables';
import { TargetTestbyDimensions } from '../../components/ReviewingTest/TargetTestbyDimensions';
import { PredictiveBanner } from '../../components/ReviewingTest/PredictiveBanner';
import { PredictiveStatusLog } from '../../components/ReviewingTest/PredictiveStatusLog';
import { ImpactSummary } from '../../components/ReviewingTest/ImpactSummary';
import { ReviewingTestSkeleton } from '../../components/ReviewingTest/ReviewingTestSkeleton';
import { GoalBreakdownRows } from '../../components/ReviewingTest/GoalBreakdownRows';
import { CustomerJourney } from '../../components/CreateReport/CustomerJourney';
import { GoalFacetCharts } from '../../components/ReviewingTest/GoalFacetCharts';
// Reused, not rebuilt: this is the same section header the Aggregated Report
// uses for GOAL BREAKDOWN / LIFECYCLE / INSIGHTS, so caps typography, chevron
// and spacing match by construction rather than by eye.
import { SectionHeader } from '../../components/Report/shared';
import { visiUserSession, setVisiUserSession } from '../../GeneralFunction';
import { modals } from '@mantine/modals';
import { STATUS, CHART, withAlpha } from '../../utils/statusTokens';
import { ALL_OPTION_VALUE, isAllOptionValue, GOAL_TYPE_TO_RADIO, baseGoalType, allGoalType, allGoalLabel, buildGoalSelectData,
         buildGroupedGoalData, composeGoalValue, parseGoalValue } from '../../utils/goalAll';

// Wraps the variations table in a horizontally scrollable box.
//
// overflowX alone is NOT enough: a <table> shrinks its columns and wraps header
// text down to its minimum content width before it will ever overflow, so the
// scrollbar never appears and the headers wrap instead - the exact outcome the
// brief rules out. minWidth:max-content pins the table to its natural unwrapped
// width, so the box scrolls rather than the table squeezing. When the table
// already fits, max-content is narrower than the box and Table's own width:100%
// still wins, so nothing changes.
// Collapses only while the new layout is on; otherwise renders a bare fragment
// so the current page keeps its exact DOM for side-by-side comparison.
function CollapseIf(
  { in: opened, transitionDuration, children }:
  { in: boolean; transitionDuration?: number; children: React.ReactNode },
) {
  return <Collapse in={opened} transitionDuration={transitionDuration}>{children}</Collapse>;
}

function ScrollIfNarrow({ children }: { children: React.ReactNode }) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [atEnd, setAtEnd] = useState(true);
  const syncEdge = useCallback(() => {
    const el = scrollRef.current;
    if (!el) { return; }
    // 1px tolerance: fractional widths never land exactly on the end.
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }, []);
  useEffect(() => {
    syncEdge();
    const el = scrollRef.current;
    if (!el) { return undefined; }
    el.addEventListener('scroll', syncEdge);
    window.addEventListener('resize', syncEdge);
    return () => {
      el.removeEventListener('scroll', syncEdge);
      window.removeEventListener('resize', syncEdge);
    };
  }, [syncEdge, children]);

  return (
    // width:100% + minWidth:0 are load-bearing: this sits inside a Mantine
    // Group (a flex container). Without them the wrapper sizes to the
    // max-content table, grows past the card and the inner box never
    // scrolls.
    <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
      <Box
        ref={scrollRef}
        sx={{
          width: '100%',
          overflowX: 'auto',
          '& > table': { minWidth: 'max-content' },
        }}
      >
        {children}
      </Box>
      {/* Signals that more columns exist; disappears once the end is reached. */}
      {!atEnd && (
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: 48,
            pointerEvents: 'none',
            background: `linear-gradient(to right, rgba(255,255,255,0), ${
              theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white
            })`,
          })}
        />
      )}
    </Box>
  );
}

function ArpuChartSkeleton({ isDark }: { isDark: boolean }) {
  const bars = [75, 55, 90, 45, 65];
  return (
    <div
      style={{
        height: '400px',
        padding: '30px 30px 60px 90px',
        boxSizing: 'border-box',
        backgroundColor: isDark ? '#1e1e1e' : '#fff',
      }}
    >
      <div style={{ height: '100%', display: 'flex', alignItems: 'flex-end', gap: 20 }}>
        {bars.map((h, i) => (
          <Skeleton key={i} height={`${h}%`} radius={4} style={{ flex: 1 }} />
        ))}
      </div>
    </div>
  );
}

export function ReviewingTest() {

  const navigate = useNavigate();
  const { websiteListData } = useAuth();
	useDocumentTitle("Visiopt - Reviewing Test");

	const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');

	const handleHeaderMenuComponentChange = (Value:any) => {
	  setSelectedMemberWebsite(Value);
	};

	const testId = '1';
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  const location = useLocation();
  const { pageId, taguchiTestId, funnelId } = location.state || {};
  // Card padding matching the Aggregated Report; its surface, border and radius
  // are already Mantine's Card defaults, so only padding needs stating.
  const layoutCardProps = { padding: 'md' } as const;
  // Section collapse state. Default open, and deliberately NOT persisted: the
  // Aggregated Report does not persist its own collapse state, so "match the
  // aggregated report" means no persistence rather than inventing a mechanism
  // only this page would have. The variations table is intentionally left
  // non-collapsible - it is the reason the page exists.
  const [predictiveOpen, setPredictiveOpen] = useState(true);
  // Page-level full-window width toggle. Persisted per user so it survives navigation and re-login.
  const [tableExpanded, setTableExpanded] = useState<boolean>(
    () => visiUserSession()?.expanded_test_view === true
  );
  const toggleTableExpanded = () => {
    const next = !tableExpanded;
    setTableExpanded(next);
    setVisiUserSession('expanded_test_view', next);
  };
  const [conversionOpen, setConversionOpen] = useState(true);
  // Item 4 - column headers matching the Aggregated Report: small, uppercase,
  // letter-spaced, grey. Applied to every header by one rule rather than
  // per-column, so the long labels ("Percentage Improvement", "Statistical
  // Significance") behave the same as the short ones instead of arbitrarily.
  // Item 4 - sticky identity columns. With 14 columns inside a ~1280px card,
  // scrolling right otherwise loses which variation a row belongs to. The sticky
  // cells take background:inherit so they pick up whatever the row already has -
  // the amber leader tint, the red paused tint, or the plain card surface - and
  // hover still works because the base row colour is set in CSS, not inline.
  const stickyCardBg = isDark ? '#1A1B1E' : '#fff';
  const stickyHoverBg = isDark ? '#25262b' : '#f8f9fa';
  const newTableSx = {
    'tbody tr': { backgroundColor: stickyCardBg },
    'tbody tr:hover': { backgroundColor: stickyHoverBg },
    'thead th': { backgroundColor: stickyCardBg },
    '& .vt-sticky': { position: 'sticky' as const, backgroundColor: 'inherit', zIndex: 2 },
    // Higher specificity than '& .vt-sticky' above, so header cells keep an
    // opaque surface instead of inheriting a transparent thead.
    '& thead .vt-sticky': { zIndex: 3, backgroundColor: stickyCardBg },
    'thead th, thead th.vt-sticky': {
      textTransform: 'uppercase' as const,
      fontSize: 11,
      letterSpacing: 0,
      fontWeight: 500,
      color: isDark ? '#909296' : '#868e96',
    },
  };
  const stickyTh = (left: number, width: number, extra: any = {}) =>
    ({ className: 'vt-sticky', style: { left, width, ...extra } });
  const [testFunnelId, setTestFunnelId] = useState(funnelId);
  const [SystemUsageLoader, setSystemUsageLoaderRaw] = useState(false);
  // Keeps the full-page loader visible smoothly between back-to-back API calls to prevent blinking.
  const loaderHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setSystemUsageLoader = useCallback((visible: boolean) => {
    if (loaderHideTimerRef.current) {
      clearTimeout(loaderHideTimerRef.current);
      loaderHideTimerRef.current = null;
    }
    if (visible) {
      setSystemUsageLoaderRaw(true);
    } else {
      loaderHideTimerRef.current = setTimeout(() => {
        setSystemUsageLoaderRaw(false);
        loaderHideTimerRef.current = null;
      }, 300);
    }
  }, []);
  useEffect(() => () => {
    if (loaderHideTimerRef.current) {
      clearTimeout(loaderHideTimerRef.current);
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [pageContentLoading, setPageContentLoading] = useState(true);
  const [timePickerInitialized, setTimePickerInitialized] = useState(false);

  const [isAudienceSizeModalOpen, setIsAudienceSizeModalOpen] = useState(false);

  const [isTrafficControlModalOpen, setIsTrafficControlModalOpen] = useState(false);
  const [isTestNotesModalOpen, setIsTestNotesModalOpen] = useState(false);

  const [isGoogleAnalyticsModalOpen, setIsGoogleAnalyticsModalOpen] = useState(false);

  const [isTestMode, setIsTestMode] = useState(false);
  
  const [isAttributionModalOpen, setIsAttributionModalOpen] = useState(false);
  const [isAttributionWindowModalOpen, setIsAttributionWindowModalOpen] = useState(false);
  const [attributionWindowDays, setAttributionWindowDays] = useState(0);

  const [isVisiSmartCodeModalOpen, setIsVisiSmartCodeModalOpen] = useState(false);
  const [isTargetTestByDimensionModelOpen, setIsTargetTestByDimensionModelOpen] = useState(false);

  const [convRedioBtn, setConvRedioBtn] = useState('1');
  const [convRadioFunnelsGoals, setConvRadioFunnelsGoals] = useState<any[]>([]);
  const [convRadioPostbackOptions, setConvRadioPostbackOptions] = useState<any[]>([]);
  const [convRadioButtonOptions, setConvRadioButtonOptions] = useState<any[]>([]);
  const [convRadioWebhookOptions, setConvRadioWebhookOptions] = useState<any[]>([]);
  const [funnelsGoals, setFunnelsGoals] = useState('');
  const [selectedButton, setSelectedButton] = useState('');
  const [postbackOptions, setPostbackOptions] = useState('');
  const [webhookOptions, setWebhookOptions] = useState('');
  const [allGoalsAvailable, setAllGoalsAvailable] = useState<Record<string, boolean | undefined>>({});
  const [allOptionValue, setAllOptionValue] = useState<string>(ALL_OPTION_VALUE);
  const [allGoalsMode, setAllGoalsMode] = useState(0);
  /* Customer Type is lazy: the API sends only an availability flag on load. Rows are fetched
     per variation the first time the user opens that row's breakdown. */
  const [customerTypeAvailable, setCustomerTypeAvailable] = useState(0);
  const [customerTypeRows, setCustomerTypeRows] = useState<Record<string, any[]>>({});
  const [customerTypeLoadingFor, setCustomerTypeLoadingFor] = useState<string | null>(null);
  const [expandedCustomerType, setExpandedCustomerType] = useState<string[]>([]);
  const [journeyAvailable, setJourneyAvailable] = useState(0);
  const [goalBreakdown, setGoalBreakdown] = useState<Record<string, any[]>>({});
  const [expandedVariations, setExpandedVariations] = useState<number[]>([]);
  const [goalBreakdownChart, setGoalBreakdownChart] = useState<Record<string, any>>({});
  const [allGoalsList, setAllGoalsList] = useState<any[]>([]);
  const [showEmptyGoals, setShowEmptyGoals] = useState(false);

  const [isConversionLogsModalOpen, setIsConversionLogsModalOpen] = useState(false);

  const [selectedTimePicker, setSelectedTimePicker] = useState(0);
	const [selectedTimePickerToDate, setSelectedTimePickerToDate] = useState('');
	const [selectedTimePickerFromDate, setSelectedTimePickerFromDate] = useState('');

  const [behavioralFilterModalOpen, setBehavioralFilterModalOpen] = useState(false);

  const [variationsBtn, setVariationsBtn] = useState('1');
  const [variablesBtn, setVariablesBtn] = useState('0');
  const [showVariablesTab, setShowVariablesTab] = useState(false);

  const [behavioralDimensionTrackingTags, setBehavioralDimensionTrackingTags] = useState({});
  const [behavioralDimensionNetworkParameters, setBehavioralDimensionNetworkParameters] = useState({});
  const [networkHierarchicalNetworkParameters, setNetworkHierarchicalNetworkParameters] = useState({});
  const [behavioralDimensionCustomTags, setBehavioralDimensionCustomTags] = useState({});
  const [behavioralDimensionSource, setBehavioralDimensionSource] = useState([]);
  const [displayFilterButton, setDisplayFilterButton] = useState(0);
  const [adsTrackingData, setAdsTrackingData] = useState({});
  const [adsTrackingNetworkData, setAdsTrackingNetworkData] = useState({});

  const [setWinnerModalOpen, setSetWinnerModalOpen] = useState(false);
  const [varIdshow, setVarId] = useState('');
  const [pageUrlShow, setPageUrlShow] = useState('');

  const [behavioralFilterArrCount, setBehavioralFilterArrCount] = useState(0);
  const [testModeShow, setTestModeShow] = useState(0);
  const [webhookSetShow, setWebhookSetShow] = useState(0);
  const [hidePauseShow, setHidePauseShow] = useState('');
  const [testNameShow, setTestNameShow] = useState('');
  const [pageIdShow, setPageIdShow] = useState('');
  const [tagichiTypeIdShow, setTagichiTypeIdShow] = useState('');
  const [testPageLinkShow, setTestPageLinkShow] = useState('');
  const [testPageTitleShow, setTestPageTitleShow] = useState('');
  const [totalUniquesShow, setTotalUniquesShow] = useState('');
  const [totalVisitsShow, setTotalVisitsShow] = useState('');
  const [totalConversionsShow, setTotalConversionsShow] = useState('');
  const [testVariationsData, setTestVariationsData] = useState([]);
  const [dataColumnChart, setDataColumnChart] = useState([]);
  const [dataColumnPercentageChart, setDataColumnPercentageChart] = useState([]);
  const [dataArpuChart, setDataArpuChart] = useState([]);
  const [pageBottomMsgShow, setPageBottomMsgShow] = useState('');
  const [selectedValue, setSelectedValue] = useState('1');
  const [testTypeShow, setTestTypeShow] = useState('');
  const [variablesTabShow, setVariablesTabShow] = useState('');
  const [testsIdShow, setTestsIdShow] = useState('');
  const [testStartDate, setTestStartDate] = useState('');
  // Days of data behind the table on screen; Impact Summary projects monthly traffic from it.
  const [impactDays, setImpactDays] = useState(0);
  const [conversionTypeShow, setConversionTypeShow] = useState('');
  const [conversionTypeValueSelected, setConversionTypeValueSelected] = useState('');
  // default goal so the initial report runs only once (no second loader flicker).
  const [conversionsDropdownLoaded, setConversionsDropdownLoaded] = useState(false);
  const [convtopageNameShow, setConvtopageNameShow] = useState('');
  const [, setMarksalestime] = useState(0);
  const [testStatusShow, setTestStatusShow] = useState('');
  const [dataLineChart, setDataLineChart] = useState([]);
  const [dataLineColorChart, setDataLineColorChart] = useState([]);
  const [testsName, setTestsName] = useState('');

  const [isResetStatsModalOpen, setIsResetStatsModalOpen] = useState(false);
  const [showViewerAccessButton, setShowViewerAccessButton] = useState(false);
  const [showNotice, setShowNotice] = useState(false);
  const [noticeAutoAdjust, setNoticeAutoAdjust] = useState(true);
  const [selectedAttributionName, setSelectedAttributionName] = useState('');
  const [timePickerLabelMap, setTimePickerLabelMap] = useState<Record<string, string>>({});

  // Predictive Analysis Phase 4 state
  const [predictiveState, setPredictiveState] = useState<any>(null);
  const [predictiveStatusLogOpen, setPredictiveStatusLogOpen] = useState(false);

  const variationRef = useRef(null);
  const variableRef = useRef(null);
  const testIconRef = useRef(null);
  const conversionToRef = useRef(null);
  const conversionToPageRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [testModeName, setTestModeName] = useState("");

  const [opened, setOpened] = useState(false);
  const defaultSelectedOptions = [
    'Uniques',
    'Visits',
    'Conv. # to page',
    'Conv. % to page',
    'Percentage Improvement',
    'Statistical Significance',
    'Total Revenue',
    'AOV',
    'EPC'
  ];
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(defaultSelectedOptions);

  const allMetricsData = [
    { value: 'Uniques', label: 'Uniques' },
    { value: 'Visits', label: 'Visits' },
    { value: 'Conv. # to page', label: 'Conversions' },
    { value: 'Conv. % to page', label: 'Conv. rate' },
    { value: 'Percentage Improvement', label: 'Percentage Improvement' },
    { value: 'Statistical Significance', label: 'Statistical Significance' },
    { value: 'Subscription Revenue', label: 'Subscription Revenue' },
    { value: 'Avg. Time Spent', label: 'Avg. Time Spent' },
    { value: 'Total Revenue', label: 'Total Revenue' },
    { value: 'AOV', label: 'AOV' },
    { value: 'EPC', label: 'EPC' },
  ];
  
  const selectedMetricsData = webhookSetShow === 1
    ? allMetricsData
  : allMetricsData.filter(option => 
      option.value !== 'Total Revenue' && 
      option.value !== 'AOV' && 
      option.value !== 'EPC'
    );

  const handleToggle = (value: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(value)
        ? prev.filter((item) => item !== value)
        : [...prev, value]
    );
  };

  useEffect(() => {
    const storedOptions = JSON.parse(localStorage.getItem('selectedFilteredOptions') || 'null');
    if (!storedOptions) {
      localStorage.setItem('selectedFilteredOptions', JSON.stringify(defaultSelectedOptions));
      setSelectedMetrics(defaultSelectedOptions);
    } else {
      // Saved preferences from before the rename still carry the old metric name; map it over
      // so the column does not silently disappear for anyone who had it selected.
      setSelectedMetrics(storedOptions.map((option: string) => option === 'Avg. Revenue per unique' ? 'EPC' : option));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('selectedFilteredOptions', JSON.stringify(selectedMetrics));
  }, [selectedMetrics]);

/* TIME PICKER AND DATE PICKER  */
  const handleselectedTimePickerComponentChange = (Value:any) => {
    setSelectedTimePicker(Value);
    setTimePickerInitialized(true);
  }

  const handleselectedTimePickerToDateChange = (Value:any) => {
    setSelectedTimePickerToDate(Value);
  }

  const handleselectedTimePickerFromDateChange = (Value:any) => {
    setSelectedTimePickerFromDate(Value);
  }

  const getStartDate = () => {
      if(testStartDate !== ''){
        const [year, month, day] = testStartDate.split('-');
        return `${year}-${month}-${day}`;
      }
    };

  const getSelectedFromDate = () => {
    if(selectedTimePickerFromDate){
      const [month, day, year] = selectedTimePickerFromDate.split('-');
      return `${year}-${month}-${day}`;
    }else{
      const [year, month, day] = testStartDate.split('-');
      return `${year}-${month}-${day}`;
    }
  };

  const getSelectedToDate = () => {
    if(selectedTimePickerToDate){
      const [month, day, year] = selectedTimePickerToDate.split('-');
      return `${year}-${month}-${day}`;
    }else{
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  };
/* END TIME PICKER AND DATE PICKER  */

/* AUDIENCE SIZE */
  const handleAudienceSizeOpen = () => {
    setIsAudienceSizeModalOpen(true);
  };

  const handleAudienceSizeClose = () => {
    setIsAudienceSizeModalOpen(false);
  };
/* END AUDIENCE SIZE */

/* TRAFFIC CONTROL */
  const handleTrafficControlOpenModal = (value:any) => {
    setIsTrafficControlModalOpen(true);
  };
  
  const handleTrafficControlCloseModal = () => {
    setIsTrafficControlModalOpen(false);
  };
/* END TRAFFIC CONTROL */

/* TEST NOTES */
  const handleTestNotesOpenModal = (value:any) => {
    setIsTestNotesModalOpen(true);
  };

  const handleTestNotesCloseModal = () => {
    setIsTestNotesModalOpen(false);
  };
/* END TEST NOTES */

/* RESET STATS */
  const handleResetStatsModalOpen = (value:any) => {
    setIsResetStatsModalOpen(true);
  };

  const handleResetStatsCloseModal = (value:any) => {
    setIsResetStatsModalOpen(false);
  };
/* END RESET STATS */

/* GOOGLE ANALYTICS */
  const handleGoogleAnalyticsOpenModal = (value:any) => {
    setIsGoogleAnalyticsModalOpen(true);
  };

  const handleGoogleAnalyticsCloseModal = () => {
    setIsGoogleAnalyticsModalOpen(false);
  };
/* END GOOGLE ANALYTICS */

/* TEST MODE*/
  const handleTestMode = () => {
    setIsTestMode(false);
    setTimeout(() => setIsTestMode(true), 0); 
  };
/* END TEST MODE*/

/* ATTRIBUTION MODEL*/
  const handleAttributionModelOpenModal = (value:any) => {
    setIsAttributionModalOpen(true);
  };

  const handleAttributionCloseModal = () => {
    setIsAttributionModalOpen(false);
    fetchSelectedAttributionName();
  };

  const handleAttributionWindowOpenModal = () => {
    setIsAttributionWindowModalOpen(true);
  };

  const handleAttributionWindowCloseModal = () => {
    setIsAttributionWindowModalOpen(false);
    fetchAttributionWindowDays();
  };

  /* Drives the "N-day window" chip in the header, so the review screen always
     says out loud when it is counting on a narrower window than everywhere else. */
  const fetchAttributionWindowDays = () => {
    if (!pageId) return;
    TestsApi.getattributionwindow({ page_id: pageId })
      .then(response => {
        if (response.ok) return response.json();
        if (response.status === 401 || response.status === 400) navigate('/logout');
      })
      .then(data => {
        if (data && data.status === 1) {
          setAttributionWindowDays(Number(data.data.lookback_days) || 0);
        }
      })
      .catch(error => console.error('API Error:', error));
  };

  useEffect(() => {
    if (selectedMemberWebsite !== '' && pageId) {
      fetchAttributionWindowDays();
    }
  }, [selectedMemberWebsite, pageId]);

  const fetchSelectedAttributionName = () => {
    if (!pageId) return;
    TestsApi.getattributionmodel({ page_id: pageId })
      .then(response => {
        if (response.ok) return response.json();
        if (response.status === 401 || response.status === 400) navigate('/logout');
      })
      .then(data => {
        if (data && data.status === 1 && Array.isArray(data.data)) {
          const selected = data.data.find((item:any) => item.selected === 'true');
          if (selected) setSelectedAttributionName(selected.attribution_name);
        }
      })
      .catch(error => console.error('API Error:', error));
  };

  useEffect(() => {
    if (selectedMemberWebsite !== '' && pageId) {
      fetchSelectedAttributionName();
    }
  }, [selectedMemberWebsite, pageId]);

  useEffect(() => {
    GeneralComponentApi.gettimepicker({ type: 'reviewing_test' })
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (data && data.status === 1 && Array.isArray(data.data) && data.data[0]) {
          const map: Record<string, string> = {};
          Object.values(data.data[0]).forEach((opt: any) => {
            map[String(opt.option_value)] = opt.option_name;
          });
          setTimePickerLabelMap(map);
        }
      })
      .catch(() => {});
  }, []);
/* END ATTRIBUTION MODEL*/

/* VISISMART CODE*/
  const handleVisiSmartCodeOpen = () => {
    setIsVisiSmartCodeModalOpen(true);
  };

  const handleVisiSmartCodeCloseModal = () => {
    setIsVisiSmartCodeModalOpen(false);
  };
/* END VISISMART CODE*/

  const handleTargetTestModelOpen = () => {
    setIsTargetTestByDimensionModelOpen(true);
  };

  const handleTargetTestModelClose = () => {
    setIsTargetTestByDimensionModelOpen(false);
  };

  const handleTestOptimizeReport = () => {
    TestOptimizeReport();
  };

  const applyAllGoalsResponse = (payload: any) => {
    const isAll = Number(payload?.all_goals_mode) === 1 && payload?.goal_breakdown && typeof payload.goal_breakdown === 'object';
    setAllGoalsMode(isAll ? 1 : 0);
    setGoalBreakdown(isAll ? payload.goal_breakdown : {});
    setGoalBreakdownChart(isAll && payload.goal_breakdown_chart ? payload.goal_breakdown_chart : {});
    setAllGoalsList(isAll && Array.isArray(payload.all_goals_list) ? payload.all_goals_list : []);
    setExpandedVariations(isAll
      ? Object.keys(payload.goal_breakdown).map((v) => Number(v)).filter((v) => !isNaN(v))
      : []);
  };

  const toggleVariationExpanded = (variationNumber: any) => {
    const variationNo = Number(variationNumber);
    setExpandedVariations((prev) =>
      prev.includes(variationNo) ? prev.filter((v) => v !== variationNo) : [...prev, variationNo]
    );
  };

  /* Collapse is free. Opening fetches the breakdown the first time only — the report's own
     load never runs the Customer Type query. */
  const toggleCustomerType = (variationNumber: any) => {
    const key = String(variationNumber);
    if (expandedCustomerType.includes(key)) {
      setExpandedCustomerType((prev) => prev.filter((v) => v !== key));
      return;
    }
    if (customerTypeRows[key]) {
      setExpandedCustomerType((prev) => [...prev, key]);
      return;
    }
    setCustomerTypeLoadingFor(key);
    TestsApi.testoptimizereport({
      system_website_id: selectedMemberWebsite,
      page_id: pageId,
      time: selectedTimePicker,
      test_goal_type: conversionTypeShow,
      convtogoal: convRedioBtn === '1' ? funnelsGoals : '',
      convtobutton: convRedioBtn === '2' ? selectedButton : '',
      convtopostback: convRedioBtn === '3' ? postbackOptions : '',
      convtowebhook: convRedioBtn === '4' ? webhookOptions : '',
      from: selectedTimePickerFromDate,
      to: selectedTimePickerToDate,
      with_customer_type: 1,
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        const rows: Record<string, any[]> = {};
        if (data?.status === 1 && Array.isArray(data.data?.test_data)) {
          data.data.test_data.forEach((v: any) => {
            if (Array.isArray(v?.customer_type) && v.customer_type.length > 0) {
              rows[String(v.test_number)] = v.customer_type;
            }
          });
        }
        setCustomerTypeRows((prev) => ({ ...prev, ...rows, [key]: rows[key] || [] }));
        setExpandedCustomerType((prev) => [...prev, key]);
      })
      .finally(() => setCustomerTypeLoadingFor(null));
  };

  const goalBreakdownForVariation = (variationNumber: any) => {
    const goals = goalBreakdown?.[String(variationNumber)];
    return Array.isArray(goals) ? goals : [];
  };

  const expandAllVariations = () => {
    setExpandedVariations(Object.keys(goalBreakdown || {}).map((v) => Number(v)).filter((v) => !isNaN(v)));
  };
  const collapseAllVariations = () => setExpandedVariations([]);

  // Columns in the variation table, so the nested goal table can span the full width
  const variationColumns = useMemo(() => {
    const cols: { key: string; label: string }[] = [
      { key: 'id',         label: 'ID' },
      { key: 'pstatus',    label: 'P Status' },
      { key: 'variations', label: 'Variations' },
    ];
    if (selectedMetrics.includes('Uniques')) cols.push({ key: 'uniques', label: 'Uniques' });
    if (selectedMetrics.includes('Visits')) cols.push({ key: 'visits', label: 'Visits' });
    if (selectedMetrics.includes('Conv. # to page')) cols.push({ key: 'conv_num', label: 'Conversions' });
    if (selectedMetrics.includes('Conv. % to page')) cols.push({ key: 'conv_pct', label: 'Conv. rate' });
    if (selectedMetrics.includes('Percentage Improvement')) cols.push({ key: 'pi', label: 'Percentage Improvement' });
    cols.push({ key: 'winner', label: 'Choose Winner' });          // always rendered
    if (selectedMetrics.includes('Statistical Significance')) cols.push({ key: 'ss', label: 'Statistical Significance' });
    if (selectedMetrics.includes('Subscription Revenue')) cols.push({ key: 'sub_rev', label: 'Subscription Revenue' });
    if (webhookSetShow !== 0) {
      if (selectedMetrics.includes('Total Revenue')) cols.push({ key: 'revenue', label: 'Total Revenue' });
      if (selectedMetrics.includes('AOV')) cols.push({ key: 'aov', label: 'AOV' });
      if (selectedMetrics.includes('EPC')) cols.push({ key: 'epc', label: 'EPC' });
    }
    if (selectedMetrics.includes('Avg. Time Spent')) cols.push({ key: 'time', label: 'Avg. Time Spent' });
    if (hidePauseShow !== 'hide') cols.push({ key: 'pause', label: 'Pause Variation' });
    return cols;
  }, [selectedMetrics, webhookSetShow, hidePauseShow]);

  const handlenetworkhierarchical = () => {
    getnetworkhierarchicaldata();
  };

  const handlebehavioraldimensionfilter = () => {
    getbehavioraldimensionfilterdata();
  };

/* CONVERSIONS TO*/

  useEffect(() => {
    if(selectedMemberWebsite !== ''){
  	  if (!pageId) {
  	    navigate('/review-tests');
  	    return;
  	  }
  	  TestsApi.getconversionsdropdown({
  	    system_website_id: selectedMemberWebsite,
  	    page_id:pageId,
  	    taguchi_id:taguchiTestId
  	  })
  	  .then(response => {
  	    if (response.ok) {
  	      return response.json();
  	    } else {
  	      if (response.status === 401 || response.status === 400) {
  	        navigate("/logout");
  	      }
  	    }
  	  })
  	  .then(data => {
  	    if (data.status === 1) {
          setConvRadioFunnelsGoals(data.data.funnels_goals || []);
          setConvRadioPostbackOptions(data.data.postback_options || []);
          setConvRadioButtonOptions(data.data.button_options || []);
          setConvRadioWebhookOptions(data.data.webhook_options || []);
          setAllGoalsAvailable(data.data.all_goals_available || {});
          setAllOptionValue(data.data.all_option_value || ALL_OPTION_VALUE);
          setConversionTypeShow(data.data.conversion_type);
          setConversionTypeValueSelected(data.data.conversion_type_value_selected);
          setShowViewerAccessButton(data.data.show_access_button);
          setTestModeName(data.data.test_mode_name);
          setConversionsDropdownLoaded(true);
  	    } else {
  	      console.log("Error:","Error");
  	    }
  	  })
  	  .catch(error => {
  	    console.error("API Error:", error);
  	  });
    }
  }, [selectedMemberWebsite]);

  const funnelGoal = useMemo(
    () => buildGoalSelectData(convRadioFunnelsGoals, 'goal_id', 'goal_name', 'goal', allGoalsAvailable.goal, allOptionValue),
    [convRadioFunnelsGoals, allGoalsAvailable]);

  const postbackOption = useMemo(
    () => buildGoalSelectData(convRadioPostbackOptions, 'postback_id', 'postback_name', 'postback', allGoalsAvailable.postback, allOptionValue),
    [convRadioPostbackOptions, allGoalsAvailable]);

  const buttonOptions = useMemo(
    () => buildGoalSelectData(convRadioButtonOptions, 'button_id', 'button_name', 'button', allGoalsAvailable.button, allOptionValue),
    [convRadioButtonOptions, allGoalsAvailable]);

  const webhookOption = useMemo(
    () => buildGoalSelectData(convRadioWebhookOptions, 'webhook_id', 'webhook_name', 'webhook', allGoalsAvailable.webhook, allOptionValue),
    [convRadioWebhookOptions, allGoalsAvailable]);

  useEffect(() => {
    const selectedBaseType = baseGoalType(conversionTypeShow);
    if(selectedBaseType === 'goal'){
      setConvRedioBtn('1');
      const selectedOption = funnelGoal.find(option => option.value === conversionTypeValueSelected);
      if (selectedOption) {
        setFunnelsGoals(selectedOption.value);
      }
    }
    if(selectedBaseType === 'button'){
      setConvRedioBtn('2');
      const selectedOption = buttonOptions.find(option => option.value === conversionTypeValueSelected);
      if (selectedOption) {
        setSelectedButton(selectedOption.value);
      }
    }
    if(selectedBaseType === 'postback'){
      setConvRedioBtn('3');
      const selectedOption = postbackOption.find(option => option.value === conversionTypeValueSelected);
      if (selectedOption) {
        setPostbackOptions(selectedOption.value);
      }
    }
    if(selectedBaseType === 'webhook'){
      setConvRedioBtn('4');
      const selectedOption = webhookOption.find(option => option.value === conversionTypeValueSelected);
      if (selectedOption) {
        setWebhookOptions(selectedOption.value);
      }
    }
  }, [conversionTypeValueSelected]);

  const groupedGoalData = useMemo(
    () => buildGroupedGoalData(
      { goal: convRadioFunnelsGoals, webhook: convRadioWebhookOptions,
        postback: convRadioPostbackOptions, button: convRadioButtonOptions },
      allGoalsAvailable,
      allOptionValue
    ),
    [convRadioFunnelsGoals, convRadioWebhookOptions, convRadioPostbackOptions, convRadioButtonOptions, allGoalsAvailable, allOptionValue]
  );

  const selectedGoalValue = useMemo(() => {
    const base = baseGoalType(conversionTypeShow);
    if (!base || !conversionTypeValueSelected) return null;
    const composite = composeGoalValue(base, conversionTypeValueSelected);
    return groupedGoalData.some((o) => o.value === composite) ? composite : null;
  }, [conversionTypeShow, conversionTypeValueSelected, groupedGoalData]);

  // Name of the selected goal, used in the Late Conversion Protection messages
  const selectedGoalLabel = useMemo(() => {
    const lists: Record<string, any[]> = { goal: funnelGoal, button: buttonOptions, postback: postbackOption, webhook: webhookOption };
    const opt = (lists[baseGoalType(conversionTypeShow)] || []).find((o: any) => o.value === conversionTypeValueSelected);
    return opt ? String(opt.label) : '';
  }, [conversionTypeShow, conversionTypeValueSelected, funnelGoal, buttonOptions, postbackOption, webhookOption]);

  const handleSelectConvChange = (composite) => {
    const parsed = parseGoalValue(composite);
    if (!parsed) return;
    const selectedBaseType = parsed.type;
    const value = parsed.value;
    setConvRedioBtn(GOAL_TYPE_TO_RADIO[selectedBaseType] || '1');
    // Picking "All <type>" switches the report to the grouped view for that type
    setConversionTypeShow(isAllOptionValue(value, allOptionValue) ? allGoalType(selectedBaseType) : selectedBaseType);
    setConversionTypeValueSelected(value);
    setFunnelsGoals('');
    setSelectedButton('');
    setPostbackOptions('');
    setWebhookOptions('');
    if (selectedBaseType === 'goal') {
      setFunnelsGoals(value);
    } else if (selectedBaseType === 'button') {
      setSelectedButton(value);
    } else if (selectedBaseType === 'postback') {
      setPostbackOptions(value);
    } else {
      setWebhookOptions(value);
    }
    // a stale grouped view must not survive a switch to a different goal
    setAllGoalsMode(0);
    setGoalBreakdown({});
    setGoalBreakdownChart({});
    setExpandedVariations([]);
  };
/* END CONVERSIONS TO*/

/* CONVERSION LOG*/
  const handleConversionLogsOpenModal = (value:any) => {
  	setIsConversionLogsModalOpen(true);
  };

  const handleConversionLogsCloseModal = () => {
    setIsConversionLogsModalOpen(false);
  };
/* END CONVERSION LOG*/

/* BEHAVIORAL DIMENSION*/
  const handleBehavioralFilterOpenModal = (value:any) => {
    setBehavioralFilterModalOpen(true);
  }

  const handleBehavioralFilterCloseModal = () => {
    setBehavioralFilterModalOpen(false);
  };
/* END BEHAVIORAL DIMENSION*/

/* VARIATIONS & VARIABLES*/
  const handleOptimize = (value:any) => {
    if(value === 'Variations'){
      setVariationsBtn('1');
      setVariablesBtn('0');
    } else if(value === 'Variables'){
      setVariationsBtn('0');
      setVariablesBtn('1');
      setShowVariablesTab(false);
      setTimeout(() => setShowVariablesTab(true), 0); 
    }
  };
/* END VARIATIONS & VARIABLES*/

/* BEHAVIORAL DIMENSION FILTER*/
    const getbehavioraldimensionfilterdata = () => {
        if(selectedMemberWebsite !== '' && pageId){
          setLoading(true);
          TestsApi.getbehavioraldimensionfilterdata({
          system_website_id: selectedMemberWebsite,
          page_id:pageId,
          taguchi_id:taguchiTestId
        })
        .then(response => {
          if (response.ok) {
            return response.text().then(text => (text ? JSON.parse(text) : null));
          }
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
          return null;
        })
        .then(data => {
          setLoading(false);
          if (!data || data.status !== 1) {
            return;
          }
          setBehavioralDimensionTrackingTags(data.data.tracking_tags);
          setBehavioralDimensionCustomTags(data.data.custom_tags);
          setBehavioralDimensionNetworkParameters(data.data.network_parameters);
          setBehavioralDimensionSource(data.data.source);
          setDisplayFilterButton(data.data.display_filter_button);
        })
        .catch(error => {
          setLoading(false);
          console.error("API Error:", error);
        });
      }
    };

    useEffect(() => {
      getbehavioraldimensionfilterdata();
  }, [selectedMemberWebsite]);
/* END BEHAVIORAL DIMENSION FILTER*/

/* NETWORK HIERARCHICAL*/
  const [selectedCustomTagsValue, setSelectedCustomTagsValue] = useState([]);
  const [selectedSourceValue, setSelectedSourceValue] = useState([]);
  const [selectedNetworkParameters, setSelectedNetworkParameters] = useState({});
  const [selectedNetworkParam, setSelectedNetworkParam] = useState({ads_network: {}});

  const handleSelectNetworkParameters = (values, network, parameterName, array_id) => {
    setSelectedNetworkParameters(prevState => {
      const updatedState = {
        ...prevState,
        [network]: {
          ...prevState[network],
          [array_id]: values,
        },
      };

      getnetworkhierarchicaldata(updatedState);
      return updatedState;
    });

    setSelectedNetworkParam(prevState => {
        return {
            ...prevState,
            ads_network: {
                ...prevState.ads_network,
                [array_id]: values
            }
        };
    });
  };

  /* CUSTOMER JOURNEY */
  const [isCustomerJourneyModalOpen, setIsCustomerJourneyModalOpen] = useState(false);
  const [customerJourneyEmail, setCustomerJourneyEmail] = useState('');
  const [customerJourneyPayload, setCustomerJourneyPayload] = useState<any>(null);
  const journeyGoalSupported = Number(journeyAvailable) === 1;
  const breakdownJourneySupported = baseGoalType(conversionTypeShow) !== 'button';

  /* fromBreakdown is explicit rather than inferred from goalId: the main table now passes
     the selected goal too, so an empty goalId no longer identifies the caller. */
  const handleCustomerJourneyOpen = (variationNumber:any, conversionValue:any, goalId:any = '', goalType:any = '', fromBreakdown:boolean = false) => {
    const journeySupported = fromBreakdown ? breakdownJourneySupported : journeyGoalSupported;
    if (!journeySupported || !(Number(conversionValue) > 0) || !(Number(variationNumber) > 0)) {
      return;
    }
    const withSelections = (values:any) => Object.keys(values || {}).reduce((acc, key) => {
      if (Array.isArray(values[key]) && values[key].length > 0) {
        acc[key] = values[key];
      }
      return acc;
    }, {});
    setCustomerJourneyEmail('');
    // Same filter context the report used, so the journey lists exactly the counted conversions
    setCustomerJourneyPayload({
      system_website_id: selectedMemberWebsite,
      page_id: pageId,
      variation_no: variationNumber,
      goal_id: goalId,
      goal_type: goalType,
      time: selectedTimePicker,
      from: selectedTimePickerFromDate,
      to: selectedTimePickerToDate,
      ads_network: withSelections(selectedNetworkParam.ads_network),
      ads_tracking_network: withSelections(adsTrackingNetworkData),
      ads_custom_network: withSelections(selectedCustomTagsValue),
      source: Array.isArray(selectedSourceValue) && selectedSourceValue.length > 0 ? selectedSourceValue : []
    });
    setIsCustomerJourneyModalOpen(true);
  };

  const handleCustomerJourneyClose = () => {
    setCustomerJourneyEmail('');
    setIsCustomerJourneyModalOpen(false);
  };
/* END CUSTOMER JOURNEY */

  const getnetworkhierarchicaldata = (selectedNetworkParameters:any = {}) => {
    if(selectedMemberWebsite !== ''){
        setSystemUsageLoader(true);
        const adsData = {};
        Object.keys(selectedNetworkParameters).forEach(network => {
          Object.keys(selectedNetworkParameters[network]).forEach((parameter, index) => {
            adsData[`ads_${parameter}`] = selectedNetworkParameters[network][parameter];
          });
        });
        TestsApi.getnetworkhierarchicaldata({
        page_id:pageId,
        taguchi_id:taguchiTestId,
        hierarchical_type:"tests",
        ...adsData
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if (data.status === 1) {
          setNetworkHierarchicalNetworkParameters(data.data.network_parameters);
          Object.keys(data.data.network_parameters).forEach(network => {
          Object.keys(data.data.network_parameters[network]).forEach((parameter, index) => {
            const selectedData = data.data.network_parameters[network][parameter];
            const selectedKeys = selectedData.dropdown_data.filter(values => values.selected === "true").map(values => values.key);
            setSelectedNetworkParameters((prevData) => {
              if (prevData !== undefined) {
                const prevNetworkData = prevData[network] || {};
                return {
                  ...prevData,
                  [network]: {
                    ...prevNetworkData,
                    [selectedData.array_id]: [
                      ...selectedKeys
                    ]
                  }
                };
              } else {
                return {
                  [network]: {
                    [selectedData.array_id]: selectedKeys
                  }
                };
              }
            });
            setSelectedNetworkParam((prevData) => {
              const data = prevData.ads_network || {};
              const arrayId = selectedData.array_id;

              return {
                ...prevData,
                ads_network: {
                  ...data,
                  [arrayId]: [...selectedKeys]
                }
              };
            });
            });
          });
        } else {
          setSystemUsageLoader(false);
          console.error("Error:", "Error");
        }
      })
      .catch(error => {
        setSystemUsageLoader(false);
        console.error("API Error:", error);
      });
      setSystemUsageLoader(false);
    }
  };
/* END NETWORK HIERARCHICAL*/

/* SET WINNER*/
  const handleSetWinner = async (varId, PageUrlShow: any) => {
    // the banner, the runoff choice bar and email links pass no address: use the variation's own page
    const row: any = testVariationsData.find((v: any) => Number(v.test_number) === Number(varId));
    setVarId(varId);
    setPageUrlShow(PageUrlShow || row?.page_url || '');
    setSetWinnerModalOpen(true);
  };

  const handleSetWinnerCloseModal = () => {
    setSetWinnerModalOpen(false);
  };
/* END SET WINNER*/

/* PREDICTIVE ANALYSIS */
  const fetchPredictiveState = () => {
    if (selectedMemberWebsite && pageId) {
      TestsApi.getpredictivestate({
        system_website_id: selectedMemberWebsite,
        page_id: pageId,
        goal_type: conversionTypeShow || '',
        goal_id: conversionTypeValueSelected || '',
      })
      .then(response => response.json())
      .then(result => {
        if (result.status === 1 && result.data) {
          setPredictiveState(result.data);
        }
      })
      .catch(() => {});
    }
  };

  // A runoff's client choice (change 31): "Keep running" closes the choice bar; the engine still calls a winner as soon
  // as the data allows
  const runoffKeepRunning = () => {
    TestsApi.runoffkeeprunning({ system_website_id: selectedMemberWebsite, page_id: pageId })
      .then(response => response.json())
      .then(result => {
        if (result.status === 1) fetchPredictiveState();
        else notifications.show({ color: 'orange', title: 'Runoff', message: result.message });
      })
      .catch(() => {});
  };
  // The choice email's buttons (change 31): Set Variation X opens the Set Winner confirmation; Keep running asks first
  const runoffLinkAction = useRef<{ setWinner: number, keepRunning: boolean }>({
    setWinner: Number(location.state?.openSetWinner) || 0,
    keepRunning: !!location.state?.openKeepRunning,
  });
  useEffect(() => {
    // wait for the results rows too: they carry the page address and arrive with the test type
    if (!predictiveState || !selectedMemberWebsite || !testVariationsData.length) return;
    const act = runoffLinkAction.current;
    if (act.setWinner > 0) {
      const v = act.setWinner; act.setWinner = 0;
      handleSetWinner(v, '');
    } else if (act.keepRunning) {
      act.keepRunning = false;
      if (predictiveState?.runoff?.choice_open && !predictiveState?.runoff?.kept_running) {
        modals.openConfirmModal({
          trapFocus: false,
          title: 'Keep running',
          children: <Text size="sm">Keep the test running? The engine calls the winner as soon as the data allows.</Text>,
          labels: { confirm: 'Keep running', cancel: 'Cancel' },
          onConfirm: runoffKeepRunning,
        });
      }
    }
  }, [predictiveState, selectedMemberWebsite, testVariationsData]);

  // Re-fetch predictive state when goal selection changes
  useEffect(() => {
    if (conversionTypeShow && conversionTypeValueSelected) {
      fetchPredictiveState();
    }
  }, [conversionTypeShow, conversionTypeValueSelected]);

  const handlePredictiveBannerDismiss = () => {
    setPredictiveState((prev: any) => prev ? { ...prev, show_banner: false } : prev);
  };

  const [primaryConcludeGoal, setPrimaryConcludeGoal] = useState<string>('');
  const [acMinDays, setAcMinDays] = useState<number>(7);
  const [acMinUv, setAcMinUv] = useState<number>(100);
  const [acMinConv, setAcMinConv] = useState<number>(10);
  const [acNotifyEmail, setAcNotifyEmail] = useState<boolean>(true);
  const [acNotifySms, setAcNotifySms] = useState<boolean>(true);
  const [acGoalLocked, setAcGoalLocked] = useState<boolean>(false);
  const [acConfirmModalOpen, setAcConfirmModalOpen] = useState<boolean>(false);
  const [acConfirmText, setAcConfirmText] = useState<string>('');
  const [acSettingsModalOpen, setAcSettingsModalOpen] = useState<boolean>(false);
  const [acSettings, setAcSettings] = useState<any>(null);

  // Build list of all available goals (exclude "All Conversions")
  const primaryGoalOptions = useMemo(() => {
    const options: { value: string; label: string }[] = [];
    convRadioFunnelsGoals.forEach((g: any) => {
      if (g.goal_id && g.goal_name !== 'All Conversions') {
        options.push({ value: 'goal||' + g.goal_id, label: g.goal_name });
      }
    });
    convRadioPostbackOptions.forEach((p: any) => {
      if (p.postback_id) {
        options.push({ value: 'postback||' + p.postback_id, label: p.postback_name + ' (Postback)' });
      }
    });
    Object.values(convRadioWebhookOptions).forEach((w: any) => {
      if (w.webhook_id) {
        options.push({ value: 'webhook||' + w.webhook_id, label: w.webhook_name + ' (Webhook)' });
      }
    });
    Object.values(convRadioButtonOptions).forEach((b: any) => {
      if (b.button_id) {
        options.push({ value: 'button||' + b.button_id, label: b.button_name + ' (Button)' });
      }
    });
    return options;
  }, [convRadioFunnelsGoals, convRadioPostbackOptions, convRadioWebhookOptions, convRadioButtonOptions]);

  // Load auto-conclude settings when modal opens
  const loadAcSettings = () => {
    if (!selectedMemberWebsite || !pageId) return;
    TestsApi.getautoconcludesettings({
      system_website_id: selectedMemberWebsite,
      page_id: pageId,
    })
    .then(response => response.json())
    .then(result => {
      if (result.status === 1 && result.data) {
        setAcSettings(result.data);
        if (result.data.primary_conclude_goal_type && result.data.primary_conclude_goal_id) {
          setPrimaryConcludeGoal(result.data.primary_conclude_goal_type + '||' + result.data.primary_conclude_goal_id);
        } else if (conversionTypeShow && conversionTypeValueSelected && !isAllOptionValue(conversionTypeValueSelected, allOptionValue)) {
          setPrimaryConcludeGoal(conversionTypeShow + '||' + conversionTypeValueSelected);
        }
        setAcMinDays(result.data.auto_conclude_min_days ?? 7);
        setAcMinUv(result.data.auto_conclude_min_uv ?? 100);
        setAcMinConv(result.data.auto_conclude_min_conv ?? 10);
        setAcNotifyEmail(result.data.auto_conclude_notify_email === 1);
        setAcNotifySms(result.data.auto_conclude_notify_sms === 1);
        setAcGoalLocked(result.data.goal_locked === true);
      }
    })
    .catch(() => {});
  };

  // Fetch settings when modal opens
  useEffect(() => {
    if (acSettingsModalOpen) loadAcSettings();
  }, [acSettingsModalOpen]);

  // A notification's "Turn off auto-conclude" button (change 36) opens the Auto-Conclude settings once the test has loaded
  const openAcFromLink = useRef<boolean>(!!(location.state && location.state.openAutoConclude));
  useEffect(() => {
    if (openAcFromLink.current && predictiveState && selectedMemberWebsite && showViewerAccessButton) {
      openAcFromLink.current = false;
      setAcSettingsModalOpen(true);
    }
  }, [predictiveState, selectedMemberWebsite, showViewerAccessButton]);

  // The email-link actions above are read once: take them out of the browser's history entry (keeping the page ids
  // the page itself reads from it), so a page refresh doesn't open the popup again
  useEffect(() => {
    const st: any = window.history.state;
    if (st?.usr && (st.usr.openSetWinner || st.usr.openKeepRunning || st.usr.openAutoConclude)) {
      const usr = { ...st.usr };
      delete usr.openSetWinner; delete usr.openKeepRunning; delete usr.openAutoConclude;
      window.history.replaceState({ ...st, usr }, '');
    }
  }, []);

  const saveAutoConcludeSettings = (overrides: any = {}) => {
    if (!selectedMemberWebsite || !pageId) return;
    const parts = (overrides.primaryGoal || primaryConcludeGoal).split('||');
    const isAll = isAllOptionValue(conversionTypeValueSelected, allOptionValue);
    const fallbackGoalType = isAll ? '' : conversionTypeShow;
    const fallbackGoalId = isAll ? '' : conversionTypeValueSelected;

    TestsApi.toggleautoconclude({
      system_website_id: selectedMemberWebsite,
      page_id: pageId,
      auto_conclude_enabled: overrides.enabled !== undefined ? overrides.enabled : (acSettings?.auto_conclude_enabled ?? 0),
      primary_conclude_goal_type: parts[0] || fallbackGoalType,
      primary_conclude_goal_id: parts[1] || fallbackGoalId,
      auto_conclude_min_days: overrides.minDays !== undefined ? overrides.minDays : acMinDays,
      auto_conclude_min_uv: overrides.minUv !== undefined ? overrides.minUv : acMinUv,
      auto_conclude_min_conv: overrides.minConv !== undefined ? overrides.minConv : acMinConv,
      auto_conclude_notify_email: overrides.notifyEmail !== undefined ? (overrides.notifyEmail ? 1 : 0) : (acNotifyEmail ? 1 : 0),
      auto_conclude_notify_sms: overrides.notifySms !== undefined ? (overrides.notifySms ? 1 : 0) : (acNotifySms ? 1 : 0),
    })
    .then(response => response.json())
    .then(result => {
      if (result.status === 1 && result.data) {
        setAcSettings(result.data);
      } else if (result.status === 0) {
        notifications.show({ color: 'orange', title: 'Auto-Conclude', message: result.message });
        if (result.data?.goal_locked) setAcGoalLocked(true);
      }
    })
    .catch(() => {});
  };

  const handleToggleAutoConclude = (enabled: boolean) => {
    if (enabled) {
      setAcConfirmModalOpen(true);
    } else {
      saveAutoConcludeSettings({ enabled: 0 });
    }
  };

  const confirmEnableAutoConclude = () => {
    setAcConfirmModalOpen(false);
    saveAutoConcludeSettings({ enabled: 1 });
  };

  // Item 4 - reconcile the two status colour systems. This page used raw emoji,
  // which each OS and browser renders in its own shade, so "green" was never the
  // same colour twice - let alone the same green as the Aggregated Report's
  // #22c55e "Good". Mapping the emoji to real tokens fixes both problems without
  // touching any of the state logic below.
  const P_STATUS_COLOR: Record<string, string> = {
    '\u{1F7E2}': STATUS.good,     // Predicted Winner
    '\u{1F7E1}': STATUS.warning,  // Caution Leader
    '\u{1F535}': STATUS.trending, // Trending Leader
    '\u{1F7E0}': STATUS.pause,    // Pause Recommended
    '\u{1F534}': STATUS.poor,     // Active (No Prediction Yet)
    '\u{23F8}':  STATUS.neutral,  // Paused
  };
  const P_STATUS_LEGEND: Array<[string, string]> = [
    ['\u{1F7E2}', 'Predicted Winner'],
    ['\u{1F7E1}', 'Leader Emerging'],
    ['\u{1F535}', 'Trending Leader'],
    ['\u{1F7E0}', 'Pause Recommended'],
    ['\u{1F534}', 'Active (No Prediction Yet)'],
    ['\u{23F8}', 'Paused'],
  ];
  const pStatusDot = (icon: string) => (
    // Padded wrapper, not a bare 10px dot: it gives the tooltip a sane anchor
    // and makes the dot comfortable to hover.
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4px 6px',
        cursor: 'default',
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: P_STATUS_COLOR[icon] || '#868e96',
        }}
      />
    </Box>
  );

  const getPStatusIcon = (variationId: number) => {
    if (!predictiveState) return { icon: '\u{1F534}', label: 'No Prediction' };
    // Paused state — takes priority over all other indicators. Checks two sources:
    //   - MySQL config (paused_variation field from test_variation_pause)
    //   - Engine state (paused_variation_ids from latest day pred_test_variation_daily_stats)
    // Either source firing means the variation is currently paused; render ⏸️.
    const variation = testVariationsData.find((v: any) => v.test_number === variationId);
    const pausedFromConfig = variation && (variation.paused_variation === '1' || Number(variation.paused_variation) === 1);
    const enginePausedIds: number[] = predictiveState.paused_variation_ids || [];
    const pausedFromEngine = enginePausedIds.includes(Number(variationId));
    if (pausedFromConfig || pausedFromEngine) {
      return { icon: '\u{23F8}', label: 'Paused' };
    }
    // Phase 5 — Pause Recommended (orange dot). Internal state is PREDICTED_LOSER
    // but the client-facing label is "Pause Recommended". The audience-size gate
    // is already applied server-side, so any variation_id in this list is visible.
    const flagged: number[] = predictiveState.pause_flagged_variation_ids || [];
    if (flagged.includes(Number(variationId))) {
      return { icon: '\u{1F7E0}', label: 'Pause Recommended' };
    }
    if (predictiveState.state === 'NO_PREDICTION') return { icon: '\u{1F534}', label: 'No Prediction' };
    const isLeading = Number(predictiveState.leading_variation_id) === Number(variationId);
    switch (predictiveState.state) {
      case 'PREDICTED_WINNER':
        return isLeading ? { icon: '\u{1F7E2}', label: 'Predicted Winner' } : { icon: '\u{1F534}', label: 'Not Leading' };
      case 'PROCEED_WITH_CAUTION':
        return isLeading ? { icon: '\u{1F7E1}', label: 'Leader Emerging' } : { icon: '\u{1F534}', label: 'Not Leading' };
      case 'TRENDING':
        return isLeading ? { icon: '\u{1F535}', label: 'Trending' } : { icon: '\u{1F534}', label: 'Not Leading' };
      default:
        return { icon: '\u{1F534}', label: 'No Prediction' };
    }
  };

  // Phase 5 — true if this variation is currently surfaced as Pause Recommended.
  // Used to elevate the existing Pause Variation button (per spec Section 4.3).
  const isPauseRecommended = (variationId: number) => {
    if (!predictiveState) return false;
    const flagged: number[] = predictiveState.pause_flagged_variation_ids || [];
    return flagged.includes(Number(variationId));
  };
/* END PREDICTIVE ANALYSIS */

/* TRACKING TAG HIERARCHICAL */
  const handleTrackingTagValues = (selectedValue:any, trackingKey:any, arrayId:any) => {
    const Value = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
    const updatedTrackingData = {
      ...adsTrackingData,
      [`ads_tracking_${arrayId}`]: Value,
    };
    setAdsTrackingData(updatedTrackingData);

    setAdsTrackingNetworkData(prev => ({
      ...prev,
      [arrayId]: Value,
    }));

    const payload: any = {
      type: 'tests',
      page_id: pageId,
    };

    Object.keys(behavioralDimensionTrackingTags).forEach((key) => {
      const param = behavioralDimensionTrackingTags[key];
      const Key = `ads_tracking_${param.array_id}`;
      payload[Key] = updatedTrackingData[Key] || [];
    });

    setSystemUsageLoader(true);
    CampaignReport.trackingnetworkheraricaldd(payload)
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if(data.status === 1){
        setBehavioralDimensionTrackingTags(data.data);
        setSystemUsageLoader(false);
      }else{
        setSystemUsageLoader(false);
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message,
        });
      }
    })
    .catch(error => {
      console.error("API Error:", error);
      setSystemUsageLoader(false);
    })
  };

  const handleSelectCustomTags = (parameterName, value, array_id) => {
    setSelectedCustomTagsValue(prevState => ({
        ...prevState,
        [`${array_id}`]: value
    }));
  };

  // All Sources is exclusive, as in the Aggregate Report: picking it clears the other picks.
  const handleSelectSource = (value) => {
    const allSource = (behavioralDimensionSource as any[]).find((item) => item.group === 'All')?.key;
    setSelectedSourceValue(allSource && value.includes(allSource) ? [allSource] : value);
  };

  const behavioralDimensionFilter = () => {
    if(selectedMemberWebsite !== ''){
      const formattedAdsCustomTags = Object.keys(selectedCustomTagsValue).reduce((acc, key) => {
          acc[key] = selectedCustomTagsValue[key];
          return acc;
      }, {});

      const formatObject = (obj: Record<number, string[]>) => {
        return Object.values(obj).some(arr => Array.isArray(arr) && arr.length > 0) ? obj : {};
      };

      setSystemUsageLoader(true);
      TestsApi.testoptimizereport({
        system_website_id:selectedMemberWebsite,
        page_id:pageId,
        time: selectedTimePicker,
        test_goal_type: conversionTypeShow,
        convtogoal:convRedioBtn === '1' ? funnelsGoals : '',
        convtobutton:convRedioBtn === '2' ? selectedButton : '',
        convtopostback:convRedioBtn === '3' ? postbackOptions : '',
        convtowebhook:convRedioBtn === '4' ? webhookOptions : '',
        ads_network: formatObject(selectedNetworkParam.ads_network),
        ads_tracking_network: adsTrackingNetworkData,
        ads_custom_network: formatObject(formattedAdsCustomTags),
        source: Array.isArray(selectedSourceValue) && selectedSourceValue.length > 0 ? selectedSourceValue : [],
        from:selectedTimePickerFromDate,
        to:selectedTimePickerToDate
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if (data.status === 1) {
          setTestVariationsData(data.data.test_data);
          applyAllGoalsResponse(data.data);
          setDataColumnChart(data.data.conversion_chart_column);
          setDataColumnPercentageChart(data.data.conversion_percentage_chart_column);
          setDataArpuChart(data.data.arpu_chart_column || []);
          setDataLineChart(data.data.cumulative_conversion_over_time);
          setDataLineColorChart(data.data.cumulative_conversion_over_time_color_array);
          setTestModeShow(data.data.testmode);
          setBehavioralFilterArrCount(data.data.behavioral_filter_arr_count);
          setWebhookSetShow(data.data.webhook_set);
          setCustomerTypeAvailable(data.data.customer_type_available || 0);
          setCustomerTypeRows({});
          setExpandedCustomerType([]);
          setHidePauseShow(data.data.hide_pause);                      
          setTestNameShow(data.data.test_name);
          setPageIdShow(data.data.page_id);
          setTagichiTypeIdShow(data.data.tagichi_type_id);
          setTestFunnelId(data.data.test_funnel_id);
          setTestPageLinkShow(data.data.test_page_link);
          setTestPageTitleShow(data.data.test_page_title);
          setTotalUniquesShow(data.data.total_uniques);
          setTotalVisitsShow(data.data.total_visits);
          setTotalConversionsShow(data.data.total_conversions);
          setJourneyAvailable(data.data.customer_journey_available);
          setPageBottomMsgShow(data.data.page_bottom_msg);
          setConvtopageNameShow(data.data.convtopage_name);
          setTestTypeShow(data.data.test_type);
          setVariablesTabShow(data.data.hide_variables_tab);
          setTestsIdShow(data.data.tests_id);
          setTestStartDate(data.data.test_start_date);
          setImpactDays(Number(data.data.impact_days) || 0);
          setMarksalestime(data.data.updated_time);
          setTestStatusShow(data.data.testpausestatus);
          setTestsName(data.data.tests_name);
          setShowNotice(data.data.show_message);
          setNoticeAutoAdjust(data.data.auto_adjust !== 0);
          setSystemUsageLoader(false);
        } else {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
          setSystemUsageLoader(false);
        }
      })
      .catch(error => {
        setSystemUsageLoader(false);
        console.error("API Error:", error);
      })
      .finally(() => {
        if (pageContentLoading) {
          if (loaderHideTimerRef.current) {
            clearTimeout(loaderHideTimerRef.current);
            loaderHideTimerRef.current = null;
          }
          setSystemUsageLoaderRaw(false);
          setPageContentLoading(false);
        }
      });
    }
  };
/* END FILTER */

/* TEST OPTIMIZE REPORT*/
  const TestOptimizeReport = () => {
    if(selectedMemberWebsite !== ''){
      setSystemUsageLoader(true);
      const formattedAdsCustomTags = Object.keys(selectedCustomTagsValue).reduce((acc, key) => {
          acc[key] = selectedCustomTagsValue[key];
          return acc;
      }, {});
      TestsApi.testoptimizereport({
        system_website_id:selectedMemberWebsite,
        page_id:pageId,
        time: selectedTimePicker,
        test_goal_type: conversionTypeShow,
        convtogoal:convRedioBtn === '1' ? funnelsGoals : '',
        convtobutton:convRedioBtn === '2' ? selectedButton : '',
        convtopostback:convRedioBtn === '3' ? postbackOptions : '',
        convtowebhook:convRedioBtn === '4' ? webhookOptions : '',
        ads_network: selectedNetworkParam.ads_network,
        ads_tracking_network:adsTrackingNetworkData,
        ads_custom_network:formattedAdsCustomTags,
        source:selectedSourceValue,
        from:selectedTimePickerFromDate,
        to:selectedTimePickerToDate
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if (data.status === 1) {
          setSystemUsageLoader(false);
          setTestVariationsData(data.data.test_data);
          applyAllGoalsResponse(data.data);
          setDataColumnChart(data.data.conversion_chart_column);
          setDataColumnPercentageChart(data.data.conversion_percentage_chart_column);
          setDataArpuChart(data.data.arpu_chart_column || []);
          setDataLineChart(data.data.cumulative_conversion_over_time);
          setDataLineColorChart(data.data.cumulative_conversion_over_time_color_array);
          setTestModeShow(data.data.testmode);
          setBehavioralFilterArrCount(data.data.behavioral_filter_arr_count);
          setWebhookSetShow(data.data.webhook_set);
          setCustomerTypeAvailable(data.data.customer_type_available || 0);
          setCustomerTypeRows({});
          setExpandedCustomerType([]);
          setHidePauseShow(data.data.hide_pause);
          setTestNameShow(data.data.test_name);
          setPageIdShow(data.data.page_id);
          setTagichiTypeIdShow(data.data.tagichi_type_id);
          setTestFunnelId(data.data.test_funnel_id);
          setTestPageLinkShow(data.data.test_page_link);
          setTestPageTitleShow(data.data.test_page_title);
          setTotalUniquesShow(data.data.total_uniques);
          setTotalVisitsShow(data.data.total_visits);
          setTotalConversionsShow(data.data.total_conversions);
          setJourneyAvailable(data.data.customer_journey_available);
          setPageBottomMsgShow(data.data.page_bottom_msg);
          setConvtopageNameShow(data.data.convtopage_name);
          setTestTypeShow(data.data.test_type);
          setVariablesTabShow(data.data.hide_variables_tab);
          setTestsIdShow(data.data.tests_id);
          setTestStartDate(data.data.test_start_date);
          setImpactDays(Number(data.data.impact_days) || 0);
          setMarksalestime(data.data.updated_time);
          setTestStatusShow(data.data.testpausestatus);
          setTestsName(data.data.tests_name);
          setShowNotice(data.data.show_message);
          setNoticeAutoAdjust(data.data.auto_adjust !== 0);
        } else {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
          setSystemUsageLoader(false);
        }
      })
      .catch(error => {
        setSystemUsageLoader(false);
        console.error("API Error:", error);
      });
    }
  };

/* EXPORT */
  const getDateRangeLabel = () => {
    if (selectedTimePicker === -2 && selectedTimePickerFromDate && selectedTimePickerToDate) {
      return `${selectedTimePickerFromDate} to ${selectedTimePickerToDate}`;
    }
    const label = timePickerLabelMap[String(selectedTimePicker)];
    return label || '';
  };

  const getSelectedNetworkParametersRows = (): [string, string][] => {
    const rows: [string, string][] = [];
    const selected: any = selectedNetworkParameters || {};
    Object.keys(selected).forEach((network) => {
      const byArrayId = selected[network] || {};
      const live: any = (networkHierarchicalNetworkParameters as any)[network] || {};
      const base: any = (behavioralDimensionNetworkParameters as any)[network] || {};
      Object.keys(byArrayId).forEach((arrayId) => {
        const selectedKeys: string[] = Array.isArray(byArrayId[arrayId]) ? byArrayId[arrayId] : [];
        if (selectedKeys.length === 0) return;

        let paramName = '';
        let dropdownData: any[] = [];
        const findIn = (src: any) => {
          Object.keys(src).forEach((paramKey) => {
            const p = src[paramKey];
            if (p && String(p.array_id) === String(arrayId)) {
              paramName = paramKey;
              dropdownData = p.dropdown_data || [];
            }
          });
        };
        findIn(live);
        if (!paramName) findIn(base);

        const labels = selectedKeys.map((key) => {
          const item = dropdownData.find((d: any) => String(d.key) === String(key));
          return item ? item.value : key;
        });

        const label = `Network Param - ${network}${paramName ? ` - ${paramName}` : ''}`;
        rows.push([label, labels.join(', ')]);
      });
    });
    return rows;
  };

  const getExportColumnsFor = (webhookSet: number, hidePause: string) => {
    const cols: { header: string; key: string }[] = [
      { header: 'ID', key: 'id' },
      { header: 'P Status', key: 'p_status' },
      { header: 'Variations', key: 'variations' },
      { header: 'Uniques', key: 'unique' },
      { header: 'Visits', key: 'visit' },
      { header: 'Conversions', key: 'pageconvnum' },
      { header: 'Conv. rate', key: 'pageconv' },
      { header: 'Percentage Improvement', key: 'pi_per' },
      { header: 'Statistical Significance', key: 'ss_per' },
      { header: 'Subscription Revenue', key: 'sub_revenue' },
    ];
    if (webhookSet !== 0) {
      cols.push({ header: 'Total Revenue', key: 'revenue' });
      cols.push({ header: 'AOV', key: 'aov' });
      cols.push({ header: 'EPC', key: 'avg_revenue_per_unique' });
    }
    cols.push({ header: 'Avg. Time Spent', key: 'time_spent' });
    if (hidePause !== 'hide') {
      cols.push({ header: 'Pause Variation', key: 'pause_variation' });
    }
    return cols;
  };

  const fetchExportReportData = (): Promise<any> => {
    const formattedAdsCustomTags = Object.keys(selectedCustomTagsValue).reduce((acc: any, key) => {
      acc[key] = (selectedCustomTagsValue as any)[key];
      return acc;
    }, {});
    return TestsApi.testoptimizereportexport({
      system_website_id: selectedMemberWebsite,
      page_id: pageId,
      time: selectedTimePicker,
      ads_network: selectedNetworkParam.ads_network,
      ads_tracking_network: adsTrackingNetworkData,
      ads_custom_network: formattedAdsCustomTags,
      source: selectedSourceValue,
      from: selectedTimePickerFromDate,
      to: selectedTimePickerToDate,
    })
      .then((r: any) => (r && r.ok ? r.json() : null))
      .then((data: any) => (data && data.status === 1 ? data.data : null))
      .catch(() => null);
  };

  const sanitizeTabName = (typeLabel: string, name: string, used: Set<string>): string => {
    const raw = `${typeLabel} - ${name}`;
    let base = raw.replace(/[\\/?*[\]:]/g, ' ').trim();
    if (!base) base = typeLabel || 'Sheet';
    if (base.length > 31) base = base.substring(0, 31);
    let final = base;
    let i = 2;
    while (used.has(final)) {
      const suffix = ` (${i++})`;
      final = (base.length + suffix.length > 31 ? base.substring(0, 31 - suffix.length) : base) + suffix;
    }
    used.add(final);
    return final;
  };

  const buildExportRow = (element: any) => {
    const pStatus = getPStatusIcon(element.test_number);
    const piPer = (element.paused_variation === 0 || element.paused_variation === '1') && element.pi_per === 'Not Enough Data'
      ? element.pi_per
      : (element.paused_variation === 0 && typeof element.pi_per === 'number' ? `${Math.round(element.pi_per)}%` : '');
    const ssPer = formatStatisticalSignificance(element.ss_per);
    return {
      id: element.test_number,
      p_status: pStatus.label,
      variations: element.name,
      unique: element.unique,
      visit: element.visit,
      pageconvnum: element.pageconvnum,
      pageconv: element.pageconv !== undefined && element.pageconv !== '' ? `${element.pageconv} %` : '',
      pi_per: piPer,
      ss_per: (element.ss_class !== '' || element.ss_per !== 0) && element.ss_per !== '' ? `${ssPer}%` : '',
      sub_revenue: element.sub_revenue,
      revenue: element.revenue,
      aov: element.aov,
      avg_revenue_per_unique: element.avg_revenue_per_unique,
      time_spent: element.time_spent,
      pause_variation: (element.paused_variation === '1' || element.paused_variation === 1) ? 'Paused' : 'Active',
    };
  };

  const formatStatisticalSignificance = (value: unknown) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue >= 100 ? '99.99' : value;
  };

  /* Customer Type sub-rows for the export, same shape and same figures as the on-screen
     breakdown. Empty for non-purchase goals. */
  const buildCustomerTypeExportRows = (element: any) => {
    if (!Array.isArray(element?.customer_type) || element.customer_type.length === 0) return [];
    /* Same rule as the on-screen link: nothing to split when there are no conversions. */
    if (!(Number(element.pageconvnum) > 0)) return [];
    const uniq = Number(element.unique) || 0;
    return element.customer_type.map((ct: any) => {
      const conv = Number(ct.conversions) || 0;
      const rev = Number(ct.revenue) || 0;
      return {
        id: '',
        p_status: '',
        variations: `    ${ct.customer_type_name}`,
        unique: '',
        visit: '',
        pageconvnum: conv,
        pageconv: uniq > 0 ? `${((conv / uniq) * 100).toFixed(2)} %` : '',
        pi_per: '',
        ss_per: '',
        sub_revenue: (Number(ct.sub_revenue) || 0).toFixed(2),
        revenue: rev.toFixed(2),
        aov: conv > 0 ? (rev / conv).toFixed(2) : '0.00',
        avg_revenue_per_unique: uniq > 0 ? (rev / uniq).toFixed(2) : '0.00',
        time_spent: '',
        pause_variation: '',
      };
    });
  };

  const getExportFileName = (ext: string) => {
    const safeName = (testsName || testPageTitleShow || 'reviewing-test').replace(/[\\/:*?"<>|]+/g, '_');
    return `${safeName}-report.${ext}`;
  };

  const exportReportToXlsx = async () => {
    setSystemUsageLoader(true);
    try {
      const exportData = await fetchExportReportData();
      const conversions: any[] = exportData && Array.isArray(exportData.conversions) ? exportData.conversions : [];

      if (conversions.length === 0) {
        notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'No data to export.' });
        return;
      }

      const ExcelJS = (await import('exceljs')).default;
      const wb = new ExcelJS.Workbook();

      const C = {
        headerBg:   'FF4A90D9',
        headerFont: 'FFFFFFFF',
        metaBg:     'FFE8F4FD',
        metaLabel:  'FF2C5F8A',
        rowEven:    'FFF0F7FF',
        rowOdd:     'FFFAFCFF',
        border:     'FFB8CCE4',
      };

      const bd: ExcelJS.Border = { style: 'thin', color: { argb: C.border } };
      const borders = { top: bd, bottom: bd, left: bd, right: bd };

      const fill = (argb: string): ExcelJS.Fill =>
        ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

      const fnt = (bold: boolean, argb: string, size = 10): Partial<ExcelJS.Font> =>
        ({ name: 'Arial', size, bold, color: { argb } });

      const styleCell = (
        cell: ExcelJS.Cell,
        value: any,
        fillArgb: string,
        fontArgb: string,
        bold = false,
        align: ExcelJS.Alignment['horizontal'] = 'left',
      ) => {
        cell.value = value ?? null;
        cell.fill = fill(fillArgb);
        cell.font = fnt(bold, fontArgb);
        cell.border = borders;
        cell.alignment = { horizontal: align, vertical: 'middle', wrapText: true };
      };

      const displayVal = (v: any) => (v === undefined || v === null || v === '' ? '-' : v);

      const typeLabelOf = (t: string) =>
        t ? t.charAt(0).toUpperCase() + t.slice(1) : '';

      const testNameMeta = exportData.test_name || testsName || testPageTitleShow || '';
      const attributionMeta = exportData.attribution_model || selectedAttributionName || '';
      const windowMeta = attributionWindowDays > 0 ? `Lookback Window - ${attributionWindowDays} days` : 'True Customer Journey';

      const usedTabNames = new Set<string>();
      let tabsAdded = 0;

      conversions.forEach((conv: any) => {
        const testData: any[] = Array.isArray(conv.test_data) ? conv.test_data : [];
        if (testData.length === 0) return;

        const webhookSet = typeof conv.webhook_set === 'number' ? conv.webhook_set : 0;
        const hidePause = typeof conv.hide_pause === 'string' ? conv.hide_pause : '';
        const cols = getExportColumnsFor(webhookSet, hidePause);

        const typeLabel = typeLabelOf(conv.type || '');
        const convName = conv.name || conv.id || '';
        const tabName = sanitizeTabName(typeLabel, convName, usedTabNames);
        const sheet = wb.addWorksheet(tabName);
        sheet.columns = cols.map(c => ({ key: c.key, width: Math.max(16, c.header.length + 4) }));

        const meta: [string, string][] = [
          ['Test Name', testNameMeta],
          ['Conversions To', `${typeLabel}: ${convName}`],
          ['Attribution Model', attributionMeta],
          ['Attribution Window', windowMeta],
          ['Date Range', getDateRangeLabel()],
          ...getSelectedNetworkParametersRows(),
        ];

        meta.forEach((m, i) => {
          const r = sheet.getRow(i + 1);
          styleCell(r.getCell(1), m[0], C.metaBg, C.metaLabel, true);
          styleCell(r.getCell(2), displayVal(m[1]), C.metaBg, C.metaLabel, false);
          if (cols.length > 2) sheet.mergeCells(i + 1, 2, i + 1, cols.length);
          r.height = 20;
          r.commit();
        });

        const headerRowNum = meta.length + 2;
        const headerRow = sheet.getRow(headerRowNum);
        cols.forEach((c, ci) => {
          styleCell(headerRow.getCell(ci + 1), c.header, C.headerBg, C.headerFont, true, 'center');
        });
        headerRow.height = 30;
        headerRow.commit();

        let rowNum = headerRowNum + 1;
        testData.forEach((element: any, idx: number) => {
          const r = sheet.getRow(rowNum++);
          const bg = idx % 2 === 0 ? C.rowEven : C.rowOdd;
          const v = buildExportRow(element);
          cols.forEach((c, ci) => {
            styleCell(r.getCell(ci + 1), displayVal((v as any)[c.key]), bg, 'FF1A1A1A', false, ci < 3 ? 'left' : 'right');
          });
          r.height = 18;
          r.commit();

          buildCustomerTypeExportRows(element).forEach((ct: any) => {
            const cr = sheet.getRow(rowNum++);
            cols.forEach((c, ci) => {
              styleCell(cr.getCell(ci + 1), displayVal(ct[c.key]), bg, 'FF5C5F66', false, ci < 3 ? 'left' : 'right');
            });
            cr.height = 18;
            cr.commit();
          });
        });

        sheet.views = [{ state: 'frozen', xSplit: 0, ySplit: headerRowNum }];
        tabsAdded++;
      });

      if (tabsAdded === 0) {
        notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'No data to export.' });
        return;
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getExportFileName('xlsx');
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      notifications.show({ color: 'red', icon: <IconX size="1.1rem" />, title: 'Error', message: 'Export failed.' });
    } finally {
      setSystemUsageLoader(false);
    }
  };

/* END EXPORT REPORT */

  useEffect(() => {
    const defaultGoal = "SDNGWHVZbGZqOTBlaUpYL0tlS2ZxQT09";
    if (conversionsDropdownLoaded && !conversionTypeValueSelected && !funnelsGoals && !postbackOptions && !selectedButton && !webhookOptions) {
      setFunnelsGoals(defaultGoal);
      return;
    }
    if (timePickerInitialized && (funnelsGoals !== "" || postbackOptions !== "" || selectedButton !== "" || webhookOptions !== "") && selectedTimePicker !== null && selectedTimePicker !== -2) {
      behavioralDimensionFilter();
    }
    if (timePickerInitialized && selectedTimePicker === -2 && selectedTimePickerFromDate && selectedTimePickerToDate) {
      behavioralDimensionFilter();
    }
  }, [funnelsGoals, postbackOptions, selectedButton, webhookOptions, selectedTimePicker, selectedTimePickerFromDate, selectedTimePickerToDate, conversionsDropdownLoaded, timePickerInitialized]);

  const handleConversionRadioChange = (value) => {
    setSelectedValue(value);
    // behavioralDimensionFilter();
  };

  // Control stays blue, the predicted winner is green, every other variation is
  // neutral. Green means "good" everywhere else on this page, so painting losing
  // variations green contradicted that.
  //
  // Keying off the row index is safe, not an assumption: the API builds
  // chart_data and test_data inside the same loop with $i and $j initialised
  // together ($i = $j = 0) and incremented on adjacent lines in all four
  // branches, so chart row N is always test_data row N - and test_data is
  // exactly what testVariationsData holds.
  const chartBarColor = (i: number) => {
    if (i === 0) { return CHART.control; }
    const variation: any = (testVariationsData as any[])[i];
    const isWinnerBar = predictiveState?.state === 'PREDICTED_WINNER'
      && variation
      && Number(predictiveState?.leading_variation_id) === Number(variation.test_number)
      && variation.paused_variation !== '1';
    return isWinnerBar ? STATUS.good : '#A7ADB3';
  };

  const dataColumnChartRows = [
    ['Variation', 'Value', { role: 'style' }],
    // Bar colours arrive from the backend as CSS colour names ("lightpink",
    // "skyblue"), so the palette is overridden here from the shared token file
    // rather than being defined per component.
    ...(dataColumnChart as any[]).map((row: any, i: number) =>
      [row[0], row[1], chartBarColor(i)]),
  ];

  const optionsColumnChart = {
    legend: { position: 'none' },
    chartArea: { width: '90%', height: '80%' },
    backgroundColor: isDark ? '#1e1e1e' : '#fff',
    vAxis: {
      title: 'Conversions',
      titleTextStyle: { fontFamily: 'Cursive', fontSize: 16, color: isDark ? 'white' : 'black' },
      textStyle: { color: isDark ? 'white' : 'black' },
      gridlines: { color: isDark ? '#444' : '#ccc' },
      // Without this Google Charts picks its own baseline: a 50 vs 75 gap read
      // as roughly 5x instead of 1.5x.
      minValue: 0,
      viewWindow: { min: 0 },
    },
    hAxis: {
      textStyle: { color: isDark ? 'white' : 'black' },
      gridlines: { color: isDark ? '#444' : '#ccc' },
    },
  };

  const dataColumnPercentageChartRows = [
    ['Variation', 'Value', { role: 'style' }],
    ...(dataColumnPercentageChart as any[]).map((row: any, i: number) =>
      [row[0], row[1], chartBarColor(i)]),
  ];

  const optionsColumnPercentageChart = {
    legend: { position: 'none' },
    chartArea: { width: '90%', height: '80%' },
    backgroundColor: isDark ? '#1e1e1e' : '#fff',
    vAxis: {
      title: 'Conversions %',
      titleTextStyle: { fontFamily: 'Cursive', fontSize: 16, color: isDark ? 'white' : 'black' },
      textStyle: { color: isDark ? 'white' : 'black' },
      gridlines: { color: isDark ? '#444' : '#ccc' },
      minValue: 0,
      viewWindow: { min: 0 },
    },
    hAxis: {
      textStyle: { color: isDark ? 'white' : 'black' },
      gridlines: { color: isDark ? '#444' : '#ccc' },
    },
  };

  const dataArpuChartRows = [
    ['Variation', 'Value', { role: 'style' }],
    ...(Array.isArray(dataArpuChart) ? dataArpuChart : []),
  ];

  const optionsArpuChart = {
    legend: { position: 'none' },
    chartArea: { left: 90, right: 30, top: 30, bottom: 60 },
    backgroundColor: isDark ? '#1e1e1e' : '#fff',
    tooltip: { textStyle: { fontSize: 13 } },
    vAxis: {
      title: 'Avg. Revenue per Unique',
      titleTextStyle: { fontFamily: 'Cursive', fontSize: 14, italic: false, color: isDark ? 'white' : 'black' },
      textStyle: { color: isDark ? 'white' : 'black', fontSize: 12 },
      gridlines: { color: isDark ? '#444' : '#ccc' },
      format: '$#,##0.00',
      minValue: 0,
    },
    hAxis: {
      textStyle: { color: isDark ? 'white' : 'black', fontSize: 12 },
      gridlines: { color: isDark ? '#444' : '#ccc' },
    },
  };

  const getBackgroundColor = (highlight) => {
    if (highlight === 'red') {
      return isDark ? 'rgb(152, 59, 59)' : '#ffd9d9';
    }
    if (highlight === 'yellow') {
      // Accent variant keeps the original fill in both modes, per client; the
      // only difference is the left accent bar applied on the row itself.
      return isDark ? '#243f41' : '#fff7c6';
    }
    return '';
  };

  const getTextColor = (highlight) => {
    if (highlight === 'red') {
      return isDark ? '#ffffff' : '#000000';
    }
    return isDark ? '#ffffff' : '';
  };

  // Chart vs engine leader mismatch — same strict 4-criteria check as the
  // banner explainer. Only fires when the repeat-conversion story is
  // provably true (Y has more chart events than unique converters AND leader
  // is genuinely ahead on unique CR). Also gated on default date range —
  // engine's cumulative window only aligns with the chart on that view.
  const engineLeaderId = predictiveState?.leading_variation_id != null
    ? Number(predictiveState.leading_variation_id) : null;
  const engineVarData = predictiveState?.variation_engine_data || {};
  // "All Time" view has time picker value -1. Only on this view does the chart's
  // window match the engine's cumulative window, so the repeat-conversion check
  // is apples-to-apples. Any other date range → hide the mismatch to avoid a
  // false explanation based on window mismatch rather than actual repeats.
  const isDefaultDateRange = selectedTimePicker === -1;
  let hasLeaderChartMismatch = false;
  if (isDefaultDateRange
      && engineLeaderId !== null && engineVarData && engineVarData[engineLeaderId]
      && Array.isArray(testVariationsData) && testVariationsData.length > 0) {
    const leaderRow = testVariationsData.find((v: any) => Number(v.test_number) === engineLeaderId);
    const leaderConvNum = leaderRow ? Number(leaderRow.pageconvnum) || 0 : 0;
    const leaderConvPct = leaderRow ? Number(leaderRow.pageconv) || 0 : 0;
    const leaderUniqueCr = Number(engineVarData[engineLeaderId].unique_cr) || 0;
    for (const v of testVariationsData) {
      const vid = Number(v.test_number);
      if (vid === engineLeaderId) continue;
      const vConvNum = Number(v.pageconvnum) || 0;
      const vConvPct = Number(v.pageconv) || 0;
      const beatsChart = vConvNum > leaderConvNum || vConvPct > leaderConvPct;
      if (!beatsChart) continue;
      const vEngine = engineVarData[vid];
      if (!vEngine) continue;
      const hasRepeatConversions = vConvNum > (Number(vEngine.unique_conv) || 0);
      if (!hasRepeatConversions) continue;
      const leaderAheadOnUniqueCr = leaderUniqueCr >= (Number(vEngine.unique_cr) || 0);
      if (!leaderAheadOnUniqueCr) continue;
      hasLeaderChartMismatch = true;
      break;
    }
  }
  const mismatchTooltipLabel = 'This variation is the engine\'s leader because it wins on unique-visitor conversion rate, which is the statistically valid basis for choosing a winner. Other variations may show more conversions on the chart when the same visitor converted multiple times, but that doesn\'t change the underlying rate.';

  const testVariationsRows = testVariationsData.map((element,index) => {
    const pStatus = getPStatusIcon(element.test_number);
    // Pause Recommended now marks the whole row - left accent bar plus a tint
    // across the full width - instead of decorating only the Pause Variation
    // cell, which left the bar stranded mid-table. An existing highlight
    // (leader / paused) still wins, so the two treatments never fight.
    // The tint must stay OPAQUE: the sticky identity columns inherit it.
    const pauseRecommended = isPauseRecommended(element.test_number);
    const showPauseAccent = pauseRecommended && !element.column_heighlite;
    const showLeaderAccent = element.column_heighlite === 'yellow';
    const rowBackground = getBackgroundColor(element.column_heighlite)
      || (showPauseAccent ? (isDark ? '#3a2a1a' : '#fff4e6') : '');
    const isElevated = predictiveState?.state === 'PREDICTED_WINNER'
      && Number(predictiveState?.leading_variation_id) === Number(element.test_number)
      && element.paused_variation !== '1';
    const isLeaderRowWithMismatch = hasLeaderChartMismatch
      && Number(element.test_number) === engineLeaderId;
    const tdStyle = isDark ? { color: '#ffffff' } : {};
    const variationGoals = allGoalsMode === 1 ? goalBreakdownForVariation(element.test_number) : [];
    /* Customer Type sub-rows. Shown only for a webhook goal whose webhook has Count as
       Purchase enabled — the API sends an empty array in every other case. Mapped onto the
       goal row shape so the same component draws both. */
    const ctKey = String(element.test_number);
    /* A variation with no conversions at all has nothing to split, so the link is not offered.
       An absent count reads as no conversions. */
    const ctEligible = allGoalsMode === 0 && customerTypeAvailable === 1
      && Number(element.pageconvnum) > 0;
    const ctOpen = expandedCustomerType.includes(ctKey);
    const ctFetched = customerTypeRows[ctKey];
    /* The two segments are fixed, so the rows are drawn from the labels alone and a segment the
       report did not send reads as zero instead of vanishing - an empty block gave no way to tell
       "no purchases yet" apart from a broken screen. Same idiom the campaign report uses in
       renderCustomerTypeBreakdownRows. */
    const CT_SEGMENTS = ['First-time Customers', 'Returning Customers'];
    const ctSupplied = Array.isArray(ctFetched) ? ctFetched : [];
    const variationCustomerType = CT_SEGMENTS.map((name) => {
      const ct   = ctSupplied.find((x: any) => x && x.customer_type_name === name) || {};
      const conv = Number(ct.conversions) || 0;
      const rev  = Number(ct.revenue) || 0;
      const uniq = Number(element.unique) || 0;
      return {
        goal_name:   name,
        pageconvnum: conv,
        pageconv:    uniq > 0 ? ((conv / uniq) * 100).toFixed(2) : '0.00',
        revenue:     rev.toFixed(2),
        sub_revenue: (Number(ct.sub_revenue) || 0).toFixed(2),
        aov:         conv > 0 ? (rev / conv).toFixed(2) : '0.00',
        avg_revenue_per_unique: uniq > 0 ? (rev / uniq).toFixed(2) : '0.00',
        /* Improvement and significance are not reported per customer type — see the
           blankColumns passed to GoalBreakdownRows below. */
      };
    });
    const isExpanded = expandedVariations.includes(Number(element.test_number));
    const goalCellDash = <Text fz={11} c="dimmed">—</Text>;
    return (
      <React.Fragment key={element.test_number}>
      <tr
        style={{
          backgroundColor: rowBackground,
          color: getTextColor(element.column_heighlite),
        }}
      >
      <td
        className="vt-sticky"
        style={{
          left: 0,
          width: 56,
          ...tdStyle,
          ...((showLeaderAccent || showPauseAccent)
            ? { boxShadow: `inset 3px 0 0 ${showLeaderAccent ? STATUS.good : STATUS.pause}` }
            : {}),
        }}
      >
        {element.test_number}
      </td>
      <td className="vt-sticky" style={{ textAlign: 'center', left: 56, width: 88, ...tdStyle }}>
        {pStatus.icon ? (
          <Tooltip label={pStatus.label} withArrow withinPortal position="top">
            {pStatusDot(pStatus.icon)}
          </Tooltip>
        ) : null}
      </td>
      <td className="vt-sticky" style={{ left: 144, width: 200, ...tdStyle }}>
        <div style={{ display: 'flex'}}>
          {allGoalsMode === 1 && variationGoals.length > 0 && (
            <ActionIcon
              size="sm"
              variant="subtle"
              mt={4}
              mr={2}
              aria-label={isExpanded ? 'Collapse goals' : 'Expand goals'}
              title={isExpanded ? 'Collapse goals' : 'Expand goals'}
              onClick={() => toggleVariationExpanded(element.test_number)}
            >
              {isExpanded ? <IconChevronDown size="0.9rem" /> : <IconChevronRight size="0.9rem" />}
            </ActionIcon>
          )}
          <CustomTitle
            varId={element.variable}
            verId={element.version} 
            currentVariations={element.name} 
            testnumber={element.test_number}
            previewadtest={element.preview_adtest}
            editvariationhide={element.edit_variation_hide}
            pageId={pageId}
            taguchiTestId={taguchiTestId}
            testsIdShow={testsIdShow}
            setMarksalestime={setMarksalestime}
            TestOptimizeReport={handleTestOptimizeReport}
          />
        </div>
        {/* Runoff client choice (change 31): the finalists while the choice is open */}
        {predictiveState?.runoff?.choice_open && !predictiveState?.runoff?.kept_running
          && (predictiveState.runoff.finalists || []).some((f: any) => Number(f.variation_id) === Number(element.test_number)) && (
          <Badge size="xs" color="yellow" variant="light" mt={2} style={{ textTransform: 'none' }}>
            Finalist
          </Badge>
        )}
        {/* Rule 4: the variation whose lead is on hold for confirmation */}
        {Number(predictiveState?.rule4_hold?.pending_variation_id) === Number(element.test_number) && (
          <Tooltip
            label="This variation pulled ahead on one day's results. It becomes the leader once its lead holds for a few more days."
            multiline
            width={260}
            withArrow
            withinPortal
          >
            <Badge size="xs" color="gray" variant="light" mt={2} style={{ cursor: 'help', textTransform: 'none' }}>
              {'⏳ Confirming lead'}
            </Badge>
          </Tooltip>
        )}
        {ctEligible && (
          <Anchor
            component="button"
            type="button"
            fz={11}
            fw={500}
            underline={false}
            onClick={() => toggleCustomerType(element.test_number)}
            aria-expanded={ctOpen}
            disabled={customerTypeLoadingFor !== null}
            style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2 }}
          >
            {ctOpen ? <IconChevronDown size="0.75rem" /> : <IconChevronRight size="0.75rem" />}
            {customerTypeLoadingFor === ctKey ? 'Loading…' : 'Customer Type'}
          </Anchor>
        )}
      </td>
      {selectedMetrics.includes('Uniques') && <td style={tdStyle}>{element.unique}</td>}
      {selectedMetrics.includes('Visits') && <td style={tdStyle}>{element.visit}</td>}
      {selectedMetrics.includes('Conv. # to page') && (
        <td
          style={{ ...tdStyle, ...(journeyGoalSupported && allGoalsMode !== 1 && Number(element.pageconvnum) > 0 ? { cursor: 'pointer' } : {}) }}
          onClick={journeyGoalSupported && allGoalsMode !== 1 ? () => handleCustomerJourneyOpen(element.test_number, element.pageconvnum, isAllOptionValue(conversionTypeValueSelected, allOptionValue) ? '' : conversionTypeValueSelected, baseGoalType(conversionTypeShow)) : undefined}
        >
          {allGoalsMode === 1 ? goalCellDash : element.pageconvnum}
          {/* Rule 3 marker: the table keeps its count; the prediction leaves these out */}
          {allGoalsMode !== 1 && Number(predictiveState?.rule3_excluded?.[element.test_number]) > 0 && (() => {
            const n = Number(predictiveState.rule3_excluded[element.test_number]);
            return (
              <Text fz={10} c="dimmed" lh={1.2} mt={2} style={{ maxWidth: 150 }}>
                {`${n} conversion${n === 1 ? '' : 's'} excluded from prediction after checks`}
              </Text>
            );
          })()}
        </td>
      )}
      {selectedMetrics.includes('Conv. % to page') && (
        <td style={tdStyle}>
          {allGoalsMode === 1 ? goalCellDash : isLeaderRowWithMismatch ? (
            <Tooltip label={mismatchTooltipLabel} multiline width={280} withArrow>
              <span style={{ cursor: 'help', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {element.pageconv} % <IconInfoCircle size={12} style={{ opacity: 0.7 }} />
              </span>
            </Tooltip>
          ) : (
            <>{element.pageconv} %</>
          )}
        </td>
      )}
      {selectedMetrics.includes('Percentage Improvement') && (
      <td style={tdStyle}>
        {allGoalsMode === 1 ? goalCellDash :
         ((element.paused_variation === 0 || element.paused_variation === "1") &&
          element.pi_per === "Not Enough Data") ? (
            <Text
              fw={500}
              style={{
                color: isDark && element.column_heighlite === 'red'
                  ? '#d0d0d0'
                  : undefined,
              }}
              c={isDark && element.column_heighlite === 'red' ? undefined : 'dimmed'}
            >
              {element.pi_per}
            </Text>
        ) : element.paused_variation === 0 ? (
          <div style={{ display: 'flex' }}>
            <Text fw={500} w={40}>{Math.round(element.pi_per)}%</Text>
            <div className="pi_graph pi_cf">
              <div className={`pi_ext ${element.pi_class}`}>
                <div
                  style={{ width: `${Math.min(Math.round(element.pi_per_value), 100)}%` }}
                  className={`pi_bar ${element.pi_class}`}
                ></div>
              </div>
            </div>
          </div>
        ) : null}
      </td>
      )}
      <td>
        {isElevated ? (
          <Text 
            fw={700} 
            style={{
              cursor: 'pointer', 
              padding: '2px 8px', 
              borderRadius: 4, 
              display: 'inline-block',
              color: '#ffffff',
              backgroundColor: STATUS.good,
              border: `1px solid ${STATUS.good}`
            }} 
            onClick={() => handleSetWinner(element.test_number,element.page_url)}
          >
            Set Winner
          </Text>
        ) : (
          <Text
            fw={700}
            style={{
              cursor: 'pointer',
              color: isDark
                ? (element.column_heighlite === 'red' ? withAlpha(STATUS.good, 0.85) : STATUS.good)
                : STATUS.good
            }}
            onClick={() => handleSetWinner(element.test_number,element.page_url)}
          >
            Set Winner
          </Text>
        )}
      </td>
      {selectedMetrics.includes('Statistical Significance') &&
        <>
          {allGoalsMode === 1 ? (
            <td style={tdStyle}>{goalCellDash}</td>
          ) : (element.ss_class !== '' || element.ss_per !== 0) ? (
          <td style={tdStyle}><div style={{ display: 'flex'}}><Text fw={500} style={{width:"5.5rem"}}>{`${formatStatisticalSignificance(element.ss_per)}%`}</Text>{element.ss_per !== ''?<progress value={element.ss_per} style={{width:"208%",marginTop:"3px",marginLeft:"6px"}} className={element.ss_class} max="100"></progress>:''}</div></td>
          ) : (
            <td style={tdStyle}></td>
          )}
        </>
      }
      {selectedMetrics.includes('Subscription Revenue') && <td style={tdStyle}>{allGoalsMode === 1 ? goalCellDash : element.sub_revenue}</td>}
      {webhookSetShow !== 0 && (
      <>
      {selectedMetrics.includes('Total Revenue') && <td style={tdStyle}>{allGoalsMode === 1 ? goalCellDash : element.revenue}</td>}
      {selectedMetrics.includes('AOV') && <td style={tdStyle}>{allGoalsMode === 1 ? goalCellDash : element.aov}</td>}
      {selectedMetrics.includes('EPC') && <td style={tdStyle}>{allGoalsMode === 1 ? goalCellDash : element.avg_revenue_per_unique}</td>}
      </>
      )}
      {selectedMetrics.includes('Avg. Time Spent') && <td style={tdStyle}>{element.time_spent}</td>}
      {hidePauseShow !== 'hide' && (
      <>
      <td>
        <PauseVariations
          selectedMemberWebsite={selectedMemberWebsite}
          pageId={pageId}
          taguchiTestId={taguchiTestId}
          testsIdShow={testsIdShow}
          pausedvariation={element.paused_variation}
          varId={element.test_number}
          currentVariations={element.name}
          setMarksalestime={setMarksalestime}
          TestOptimizeReport={handleTestOptimizeReport}
          systemUsageLoader={setSystemUsageLoader}
        />
      </td>
       </>
      )}
    </tr>
    {allGoalsMode === 1 && isExpanded && variationGoals.length > 0 && (
      <GoalBreakdownRows
        goals={variationGoals}
        columns={variationColumns}
        isDark={isDark}
        rowKeyPrefix={`v${element.test_number}-`}
        stickyLefts={[0, 56, 144]}
        onConvNumClick={breakdownJourneySupported ? (goal:any) => handleCustomerJourneyOpen(element.test_number, goal.pageconvnum, goal.goal_id, baseGoalType(conversionTypeShow), true) : null}
      />
    )}
    {/* ctEligible as well as ctOpen: a variation left expanded whose conversions have since
        gone to zero has no link to collapse it, so the block must close itself. */}
    {ctEligible && ctOpen && (
      <GoalBreakdownRows
        goals={variationCustomerType}
        columns={variationColumns}
        isDark={isDark}
        rowKeyPrefix={`ct${element.test_number}-`}
        blankColumns={['pi', 'ss']}
        showHeading={false}
      />
    )}
    </React.Fragment>
    );
  });
/* END TEST OPTIMIZE REPORT*/

  const behavioralDimensionClearFilter = () => {
    setSystemUsageLoader(true);
    setBehavioralDimensionTrackingTags({});
    setBehavioralDimensionCustomTags({});
    setBehavioralDimensionNetworkParameters({});
    setNetworkHierarchicalNetworkParameters({});
    setBehavioralDimensionSource([]);
    setSelectedCustomTagsValue([]);
    setSelectedSourceValue([]);
    setSelectedNetworkParameters({});
    setSelectedNetworkParam({ads_network: {}});

    TestsApi.testoptimizereport({
      system_website_id:selectedMemberWebsite,
      page_id:pageId,
      time: selectedTimePicker,
      test_goal_type: conversionTypeShow,
      convtogoal:convRedioBtn === '1' ? funnelsGoals : '',
      convtobutton:convRedioBtn === '2' ? selectedButton : '',
      convtopostback:convRedioBtn === '3' ? postbackOptions : '',
      convtowebhook:convRedioBtn === '4' ? webhookOptions : '',
      from:selectedTimePickerFromDate,
      to:selectedTimePickerToDate
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if (data.status === 1) {
        setSystemUsageLoader(false);
        setTestVariationsData(data.data.test_data);
        applyAllGoalsResponse(data.data);
        setDataColumnChart(data.data.conversion_chart_column);
        setDataColumnPercentageChart(data.data.conversion_percentage_chart_column);
        setDataArpuChart(data.data.arpu_chart_column || []);
        setDataLineChart(data.data.cumulative_conversion_over_time);
        setDataLineColorChart(data.data.cumulative_conversion_over_time_color_array);
        setTestModeShow(data.data.testmode);
        setBehavioralFilterArrCount(data.data.behavioral_filter_arr_count);
        setWebhookSetShow(data.data.webhook_set);
        setCustomerTypeAvailable(data.data.customer_type_available || 0);
        setCustomerTypeRows({});
        setExpandedCustomerType([]);
        setHidePauseShow(data.data.hide_pause);
        setTestNameShow(data.data.test_name);
        setPageIdShow(data.data.page_id);
        setTagichiTypeIdShow(data.data.tagichi_type_id);
        setTestFunnelId(data.data.test_funnel_id);
        setTestPageLinkShow(data.data.test_page_link);
        setTestPageTitleShow(data.data.test_page_title);
        setTotalUniquesShow(data.data.total_uniques);
        setTotalVisitsShow(data.data.total_visits);
        setTotalConversionsShow(data.data.total_conversions);
          setJourneyAvailable(data.data.customer_journey_available);
        setPageBottomMsgShow(data.data.page_bottom_msg);
        setConvtopageNameShow(data.data.convtopage_name);
        setTestTypeShow(data.data.test_type);
        setVariablesTabShow(data.data.hide_variables_tab);
        setTestsIdShow(data.data.tests_id);
        setTestStartDate(data.data.test_start_date);
        setImpactDays(Number(data.data.impact_days) || 0);
        setMarksalestime(data.data.updated_time);
        setTestsName(data.data.tests_name);
        setShowNotice(data.data.show_message);
        setNoticeAutoAdjust(data.data.auto_adjust !== 0);
        getbehavioraldimensionfilterdata();
        fetchPredictiveState();
      } else {
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message,
        });
        setSystemUsageLoader(false);
      }
    })
    .catch(error => {
      setSystemUsageLoader(false);
      console.error("API Error:", error);
    });
  };

  const handleRefreshStats = () => {
    behavioralDimensionClearFilter();
  };

  const handleEditTest = (pageId:any,tagichiTypeId:any) => {
    if(testTypeShow === "modern_test"){
      const url = `/test-editor-visual-modern/${pageId}/${tagichiTypeId}`;
      window.open(url, '_blank');
    }else{
      const url = `/test-editor-visual/${pageId}/${tagichiTypeId}`;
      window.open(url, '_blank');
    }
  };

  const [selectedGraph, setSelectedGraph] = useState('0');

  //LineChart
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 5; // Number of legend items per page
  const dataLineChartRows = Array.isArray(dataLineChart) ? [
    ...dataLineChart,
  ] : [];
  const lineChartColors = [
    ...dataLineColorChart
  ];
  // Colors for each line (25 variations)
  const optionsLineChart = {
    legend: { position: 'none' },
    chartArea: { height: 'auto', width: '90%' },
    backgroundColor: isDark ? '#1e1e1e' : '#fff',
    vAxis: {
      title: 'Cumulative Conversions',
      titleTextStyle: { fontFamily: 'Cursive', fontSize: 16, color: isDark ? 'white' : 'black' },
      textStyle: { color: isDark ? 'white' : 'black' },
      minValue: 0,
      gridlines: { color: isDark ? '#444' : '#ccc' },
    },
    hAxis: {
      title: 'Days',
      titleTextStyle: { fontFamily: 'Cursive', fontSize: 16, color: isDark ? 'white' : 'black' },
      textStyle: { color: isDark ? 'white' : 'black' },
      gridlines: { color: isDark ? '#444' : '#ccc' },
    },
    colors: lineChartColors,
  };

  // Pagination for legend items (slicing colors and labels)
  let paginatedLabels = [];
  let paginatedColors = [];
  if(dataLineChart.length > 0){
     paginatedLabels = dataLineChart[0].slice(1).slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
     paginatedColors = lineChartColors.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);
  }
  // Calculate total pages for legend
  const charttotalPages = Math.ceil(lineChartColors.length / itemsPerPage);

  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    const refMapping = {
      25: testIconRef,
      26: variationRef,
      27: variableRef,
      28: conversionToPageRef,
      287: conversionToRef
    };
    let iconRef = refMapping[id];
    if (iconRef.current) {
      const { top, left, width } = iconRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        [id]: { top: top + scrollOffset, left: left + width + 10 },
      });
    }
  };

  // ---- Item 2: header card ---------------------------------------------
  // The Aggregated Report separates view controls from data: card 1 is what
  // you are looking at and how it is scoped, card 2+ is the data. This page
  // mixed both into one slab. Extracting the pieces lets the very same JSX
  // render as today's single card or as that header + data pair, with no
  // duplicated markup on either path.
  const tabRow = (
    <>
        {variablesTabShow === 'show' && (
          <div style={{ display: 'flex', marginBottom: '10px' }}>
		    <Box w={130}>
  		    <Button rightIcon={<IconHelp ref={variationRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={(event: any) => { event.stopPropagation(); handleHelpClick(26); }}/>} variant="default" bg={variationsBtn === '1' ? '#e7fcf5' : "" } type="submit" onClick={() => handleOptimize('Variations')}
            sx={(theme) => {
              const isGreenBg = variationsBtn === '1';
              return {
                backgroundColor: isDark ? theme.colors.dark[5] : isGreenBg ? '#e7fcf5' : '#fff',
                color: isGreenBg ? 'black' : isDark ? 'white' : 'black',
                '&:hover': { color: isDark ? 'white' : 'black' },
              };
            }}>
            Variations
          </Button>
        </Box>
          <Box w={130}>
    		    <Button rightIcon={<IconHelp ref={variableRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={(event: any) => { event.stopPropagation(); handleHelpClick(27); }}/>} variant="default" bg={variablesBtn === '1' ? '#e7fcf5' : "" }type="submit" onClick={() => handleOptimize('Variables')} 
              sx={(theme) => {
                const isGreenBg = variablesBtn === '1';
                return {
                  backgroundColor: isDark ? theme.colors.dark[5] : isGreenBg ? '#e7fcf5' : '#fff',
                  color: isGreenBg ? 'black' : isDark ? 'white' : 'black',
                  '&:hover': { color: isDark ? 'white' : 'black' },
                };
              }}>
              Variables
            </Button>
          </Box>
          </div>
        )}
    </>
  );
  // Impact Summary sits above the Variations / Variables tabs in both views. The
  // "All Conversions" roll-up gets no prediction (see PredictiveBanner), so it is
  // treated like the "All ..." views: there is no single goal to project.
  const impactSummary = (
    <ImpactSummary
      rows={testVariationsData}
      days={impactDays}
      predictiveState={predictiveState}
      goalName={convtopageNameShow}
      goalKey={`${baseGoalType(conversionTypeShow)}:${conversionTypeValueSelected}`}
      singleGoal={allGoalsMode !== 1 && conversionTypeValueSelected !== 'SDNGWHVZbGZqOTBlaUpYL0tlS2ZxQT09'}
      selectedMemberWebsite={selectedMemberWebsite}
      goalType={baseGoalType(conversionTypeShow)}
      goalId={conversionTypeValueSelected}
      pageId={pageId}
      onSelectGoal={(type, key) => handleSelectConvChange(composeGoalValue(type, key))}
    />
  );
  const hdrTitle = (
    <>
  		  		<Group position="apart" mb="xs">
  		        <Text weight={500}>TEST - {testsName || testPageTitleShow} <IconHelp ref={testIconRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(25)}/></Text>
  		        {attributionWindowDays > 0 && (
  		          <Tooltip
  		            multiline
  		            width={280}
  		            withArrow
  		            withinPortal
  		            position="bottom"
  		            label={`Conversions on this screen only count when the sale happened within ${attributionWindowDays} days of the visit. Other screens are unaffected.`}
  		          >
  		            <Badge
  		              color="teal"
  		              variant="outline"
  		              size="sm"
  		              sx={{ textTransform: 'none', cursor: 'pointer' }}
  		              onClick={handleAttributionWindowOpenModal}
  		            >
  		              {attributionWindowDays}-day window
  		            </Badge>
  		          </Tooltip>
  		        )}
  		      </Group>
    </>
  );
  // New layout labels this select from above in small grey text so it matches
  // Date Range and the Aggregated Report's labelled selects. The old path keeps
  // the inline "Conversions to:" label via hdrConversions above.
  const hdrConvSelect = (
    <Select
      value={selectedGoalValue}
      onChange={handleSelectConvChange}
      placeholder="Select a goal"
      searchable
      nothingFound="No goals found"
      maxDropdownHeight={320}
      data={groupedGoalData}
      withinPortal
      w={280}
    />
  );
  const hdrTestMode = testModeShow !== 0 ? (
    <Text weight={500} c="red">Test Mode Active</Text>
  ) : null;
  const hdrDatePicker = (
    <>
  	          <SelectTimePicker SelectTimePickerValue={selectedTimePicker} SelectTimePickerOnChange={handleselectedTimePickerComponentChange} SelectTimePickerFromDateOnChange={handleselectedTimePickerFromDateChange} SelectTimePickerToDateOnChange={handleselectedTimePickerToDateChange} SelectTestStartDate={getSelectedFromDate()} SelectTestEndDate={getSelectedToDate()} SelectStartDate={getStartDate()} SelectedPage='reviewing_test'/>
    </>
  );
  const hdrIcons = (
    <>
  		     		<ActionIcon
  		     		  variant="default"
  		     		  size="lg"
  		     		 	sx={(theme) => ({
  	               backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : "#fff",
  	             })}
  		     		  title="Refresh Stats"
  		     		  ml={5}
  		     		  onClick={handleRefreshStats}
  		     		>
  		     		  <IconRefresh size="1.1rem" />
  		     		</ActionIcon>
              <TestInfo 
                pageId={pageId}
              />
              {showViewerAccessButton && (
                <PauseResume 
                  selectedMemberWebsite={selectedMemberWebsite}
                  pageId={pageId} 
                  taguchiTestId={taguchiTestId} 
                  TestStatusShow={testStatusShow}
                  systemUsageLoader={setSystemUsageLoader}
                />
              )}
              {showViewerAccessButton && (
                <DiscardTest 
                  testId={testId} 
                  pageId={pageId} 
                  taguchiTestId={taguchiTestId} 
                  selectedMemberWebsite={selectedMemberWebsite} 
                  systemUsageLoader={setSystemUsageLoader}
                />
              )}
  		     		<Menu width={260} position="bottom-end" withinPortal={true}>
  		     		  <Menu.Target>
  		     		    <ActionIcon
      		     		  variant="default"
      		     		  size="lg"
      		     		  sx={(theme) => ({
      	               backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : "#fff",
      	             })}
      		     		  title="Settings"
      		     		  ml={5}
      		     		>
      		     		  <IconSettings size="1.1rem" />
      		     		</ActionIcon>
  		     		  </Menu.Target>

  		     		  <Menu.Dropdown>
  		     		    {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleAudienceSizeOpen}>Audience Size</Menu.Item>)}
                  <PermissionWrapper permission="behavioral_dimensions_access">
                    <Menu.Item px={10} py={6} onClick={handleBehavioralFilterOpenModal}>Behavioral Filters</Menu.Item>
                  </PermissionWrapper>
                  {showViewerAccessButton && testTypeShow !== "classic_test" && (
  		     		     <Menu.Item px={10} py={6} onClick={() => handleEditTest(pageIdShow,tagichiTypeIdShow)}>Edit Test</Menu.Item>
                  )}
  		     		    {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleResetStatsModalOpen}>Reset Stats</Menu.Item>)}
  		     		    <Menu.Item px={10} py={6} onClick={handleAttributionModelOpenModal}>Attribution Model</Menu.Item>
  		     		    <Menu.Item px={10} py={6} onClick={handleAttributionWindowOpenModal}>Attribution Window</Menu.Item>
  		     		    {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleTrafficControlOpenModal}>Traffic Control</Menu.Item>)}
  		     		    <Menu.Item px={10} py={6} onClick={exportReportToXlsx}>Export Report</Menu.Item>
  		     		    {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleGoogleAnalyticsOpenModal}>Google Analytics Integration</Menu.Item>)}
  		     		    {predictiveState && showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={() => setAcSettingsModalOpen(true)}>Auto-Conclude</Menu.Item>)}
  		     		    {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleConversionLogsOpenModal}>Logs</Menu.Item>)}
  		     		    {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleTestNotesOpenModal}>Test Notes</Menu.Item>)}
                  {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleTestMode}>{testModeName}</Menu.Item>)}
  		     		    <Menu.Item px={10} py={6} onClick={handleVisiSmartCodeOpen}>VisiSmart Code</Menu.Item>
                  {showViewerAccessButton && (<Menu.Item px={10} py={6} onClick={handleTargetTestModelOpen}>Target Test By Dimensions</Menu.Item>)}
  		     		  </Menu.Dropdown>
  		     		</Menu> 
    </>
  );
  const hdrMetrics = (
    <>
  		    <Group mt={10}>
            <div style={{ width: 280 }}>
              <Popover
                opened={opened}
                onChange={setOpened}
                position="bottom-start"
                width="target"
                trapFocus={false}
                withinPortal={true}
                transitionProps={{ transition: 'pop-top-left', duration: 150 }}
              >
                <Popover.Target>
                  <div onClick={() => setOpened((o) => !o)}>
                    <TextInput
                      readOnly
                      icon={
                        <span style={{
                          whiteSpace: 'nowrap',
                          color: isDark ? '#fff' : '#000',
                          fontSize: '0.8rem',
                        }}>
                          Select Metrics
                        </span>
                      }
                      rightSection={<span style={{ paddingRight: 10, color: isDark ? '#fff' : '#000' }}>▼</span>}
                      styles={{
                        input: {
                          cursor: 'pointer',
                          backgroundColor: isDark ? theme.colors.dark[6] : '#fff',
                          color: isDark ? '#fff' : '#000',
                          paddingLeft: 150,
                        },
                        icon: {
                          width: 120,
                          pointerEvents: 'none',
                        },
                      }}
                    />
                  </div>
                </Popover.Target>

                <Popover.Dropdown>
                  <ScrollArea style={{ height: 200 }}>
                    {selectedMetricsData.map((item) => (
                      <Checkbox
                        key={item.value}
                        label={item.label}
                        checked={selectedMetrics.includes(item.value)}
                        onChange={() => handleToggle(item.value)}
                        styles={{ root: { cursor: 'pointer', padding: '4px 8px' } }}
                      />
                    ))}
                  </ScrollArea>
                  <Button fullWidth size="xs" mt="xs" onClick={() => setOpened(false)} variant="light">
                    Done
                  </Button>
                </Popover.Dropdown>
              </Popover>
            </div>
    		    <Box w={210}>
            {behavioralFilterArrCount !== 0 && (
              <Button fullWidth variant="default" bg="#e7fcf5"
                sx={(theme) => ({
                  backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : '#fff',
                  color: theme.colorScheme === 'dark' ? 'black' : 'inherit',
                  '&:hover': {
                    color: theme.colorScheme === 'dark' ? 'white' : 'inherit',
                  },
                })}
                onClick={handleBehavioralFilterOpenModal}
              >
                Behavioral Filters Applied
              </Button>
            )}
  	        </Box>
  		    </Group>
    </>
  );
  const hdrBehavioral = (
    <>
            <div className="ads_network">

            {Object.keys(behavioralDimensionNetworkParameters).length > 0 && (
              <>
              <Text weight={500} mt={15}>Network Parameters</Text>
              {Object.keys(behavioralDimensionNetworkParameters).map(network => (
                <div key={network} style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', marginBottom: '10px' }}>
                    <Text weight={500} mt={4} tt="capitalize">{network}:</Text>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
                      {Object.keys(behavioralDimensionNetworkParameters[network]).map(parameterName => {
                        const parameter = networkHierarchicalNetworkParameters[network]?.[parameterName] 
                          ? networkHierarchicalNetworkParameters[network][parameterName] 
                          : behavioralDimensionNetworkParameters[network]?.[parameterName];
                        return (
                          <div key={`${network}-${parameterName}`} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px' }}>
                            <Text color="red" mx={5} mt={4}>{parameterName}</Text>
                            <input type="hidden" name={`${network}-${parameterName}-id`} value={parameter.array_id} />
                            <MultiSelect
                              data={parameter.dropdown_data.map(item => ({
                                value: item.key,
                                label: item.value
                              }))}
                              onChange={(values) => handleSelectNetworkParameters(values, network, parameterName, parameter.array_id)}
                              value={selectedNetworkParameters?.[network]?.[parameter.array_id] || []}
                              name={`${network}-${parameterName}`}
                              nothingFound="No matches found"
                              dropdownPosition="bottom"
                              withinPortal
                              searchable
                              radius="sm"
                              size="sm"
                              mr={10}
                              w={200}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
              </>
            )}
            {Object.keys(behavioralDimensionTrackingTags).length > 0 && (
              <>
              <Text weight={500} mt={15}>Tracking tags</Text>
              <div className="ads_network" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginTop:'10px' }}>
                {Object.keys(behavioralDimensionTrackingTags).map(parameterName => {
                  const parameter = behavioralDimensionTrackingTags[parameterName];
                  return (
                    <div key={`${parameterName}`} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '5px' }}>
                      <Text color="red" mx={5} mt={4}>{parameterName}</Text>
                      <input type="hidden" name={`${parameterName}-id`} value={parameter.array_id} />
                      <MultiSelect
                        data={parameter.dropdown_data.map(item => ({
                          value: item.key,
                          label: item.value
                        }))}
                        onChange={(value) => handleTrackingTagValues(value, parameterName, parameter.array_id)}
                        name={parameterName}
                        nothingFound="No matches found"
                        dropdownPosition="bottom"
                        withinPortal
                        searchable
                        radius="sm"
                        size="sm"
                        mr={10}
                      />
                    </div>
                  );
                })}
              </div>
             </>
            )}            
            {Object.keys(behavioralDimensionCustomTags).length > 0 && (
              <>
              <Text weight={500} mt={15}>Custom tags</Text>
              <div className="ads_network" style={{ display: 'flex', marginTop: '10px' }}>
                {Object.keys(behavioralDimensionCustomTags).map(parameterName => {
                  const parameter = behavioralDimensionCustomTags[parameterName];
                  return (
                    <div key={`${parameterName}`} style={{ display: 'flex', marginRight: '15px' }}>
                      <Text color="red" mx={5} mt={4}>{parameterName}</Text>
                      <input type="hidden" name={`${parameterName}-id`} value={parameter.array_id} />
                      <MultiSelect
                        data={parameter.dropdown_data.map(item => ({
                          value: item.key,
                          label: item.value
                        }))}
                        onChange={(value) => handleSelectCustomTags(parameterName, value, parameter.array_id)}
                        name={parameterName}
                        nothingFound="No matches found"
                        dropdownPosition="bottom"
                        withinPortal
                        searchable
                        radius="sm"
                        size="sm"
                        mr={10}
                      />
                    </div>
                  );
                })}
              </div>
            </>
            )}
            {Object.keys(behavioralDimensionSource).length > 0 && (
              <>
              <Text weight={500} mt={15}>Traffic Source</Text>
              <div className="ads_network" style={{ display: 'flex', marginTop: '10px' }}>
                <Text color="red" mx={5} mt={4}>Traffic Source</Text>
                <MultiSelect
                  // Grouped All / Paid / Organic / Direct / Other; while All Sources is picked it is the only option.
                  data={(behavioralDimensionSource as any[])
                    .filter((item) => !(selectedSourceValue as string[]).some((v) => (behavioralDimensionSource as any[]).some((o) => o.key === v && o.group === 'All')) || item.group === 'All')
                    .map(item => ({
                      value: item.key,
                      label: item.value,
                      group: item.group
                    }))}
                  value={selectedSourceValue}
                  onChange={handleSelectSource}
                  nothingFound="No matches found"
                  dropdownPosition="bottom"
                  withinPortal
                  searchable
                  radius="sm"
                  size="sm"
                  mr={10}
                />
              </div>
              </>
            )}
            {displayFilterButton === 1 &&(
              <Button style={{ marginTop: '15px' }} onClick={() => behavioralDimensionFilter()}>
                Filter
              </Button>
            )}
          </div>
    </>
  );
  const variationsCardBody = (
    <>
          {/* Predictive Winner Detection™ - promoted from a grey caption to a
              real section header. It is the most differentiated feature on the
              page and was reading like a caption for the banner below it. */}
          <Box mt={12}>
            <SectionHeader
              icon={<IconBulb size="1rem" color="#a855f7" />}
              label="Predictive Winner Detection™"
              actionNode={
                <Button
                  component="div"
                  variant="default"
                  size="xs"
                  leftIcon={
                    tableExpanded
                      ? <IconArrowsRightLeft size="0.9rem" />
                      : <IconArrowsHorizontal size="0.9rem" />
                  }
                  title={tableExpanded ? 'Shrink the report to the standard width' : 'Expand the report to the full window width'}
                  onClick={(event: any) => { event.stopPropagation(); toggleTableExpanded(); }}
                >
                  {tableExpanded ? 'Shrink' : 'Expand'}
                </Button>
              }
              opened={predictiveOpen}
              toggle={() => setPredictiveOpen((o) => !o)}
            />
          </Box>
          {/* Predictive Analysis Banner */}
          <CollapseIf in={predictiveOpen}>
          {/* Rule 4: a leader change on hold — slim bar, not dismissable while the hold lasts */}
          {predictiveState?.rule4_hold && (() => {
            const nm = (v: number) => (Number(v) === 1 ? 'Control' : (predictiveState?.variation_names?.[Number(v)] || `Variation ${v}`));
            const pending = nm(predictiveState.rule4_hold.pending_variation_id);
            return (
              <Box
                sx={(theme) => ({
                  borderLeft: theme.colorScheme === 'dark' ? '4px solid #5c5f66' : '4px solid #adb5bd',
                  backgroundColor: theme.colorScheme === 'dark' ? '#2c2e33' : '#f1f3f5',
                  padding: '6px 16px',
                  marginTop: 12,
                  borderRadius: 4,
                })}
              >
                <Text size="sm">
                  {`⏳ ${pending} pulled ahead on one day's results. ${nm(predictiveState.rule4_hold.leader_variation_id)} stays the leader until ${pending}'s lead holds for a few more days. Results update nightly.`}
                </Text>
              </Box>
            );
          })()}
          {/* Runoff client choice (change 31) — slim bar, not dismissable while the choice is open */}
          {predictiveState?.runoff?.choice_open && !predictiveState?.runoff?.kept_running && (() => {
            const ro = predictiveState.runoff;
            const nm = (v: number) => (Number(v) === 1 ? 'Control' : (predictiveState?.variation_names?.[Number(v)] || `Variation ${v}`));
            const fins: any[] = ro.finalists || [];
            const pct = (x: any) => `${x > 0 ? '+' : ''}${x}%`;
            const parts = fins.map((f: any, i: number) => `${nm(f.variation_id)} (${Number(f.cr).toFixed(1)}%${f.lift_vs_control !== null && f.lift_vs_control !== undefined ? `, ${pct(f.lift_vs_control)}${i === 0 ? ' vs Control' : ''}` : ''})`);
            const list = parts.length <= 1 ? (parts[0] || '') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
            return (
              <Box
                sx={(theme) => ({
                  borderLeft: '4px solid #fab005',
                  backgroundColor: theme.colorScheme === 'dark' ? '#3a3520' : '#fff9db',
                  padding: '8px 16px',
                  marginTop: 12,
                  borderRadius: 4,
                })}
              >
                <Text size="sm">
                  {`Runoff day ${ro.day} — no clear winner yet. These finalists are too close to call: ${list}. Pick one based on your business preference, or keep the test running.`}
                </Text>
                <Group spacing="sm" mt={6}>
                  {fins.map((f: any) => (
                    <Button key={f.variation_id} size="xs" variant="light" color="yellow" onClick={() => handleSetWinner(f.variation_id, '')}>
                      {`Set ${nm(f.variation_id)}`}
                    </Button>
                  ))}
                  <Button size="xs" variant="default" onClick={runoffKeepRunning}>Keep running</Button>
                </Group>
              </Box>
            );
          })()}
          <PredictiveBanner
            predictiveState={predictiveState}
            pageId={pageId}
            selectedMemberWebsite={selectedMemberWebsite}
            onDismiss={handlePredictiveBannerDismiss}
            onSetWinner={(varId, pageUrl) => handleSetWinner(varId, pageUrl)}
            goalType={conversionTypeShow}
            goalId={conversionTypeValueSelected}
            goalLabel={selectedGoalLabel}
            testVariationsData={testVariationsData}
            isDefaultDateRange={selectedTimePicker === -1}
          />
          {/* Predictive Status Log Link */}
          {predictiveState && (
            <Group spacing="md" mb={10} mt={5}>
              <Anchor size="xs" onClick={() => setPredictiveStatusLogOpen(true)}>
                View Status Log
              </Anchor>
            </Group>
          )}
          </CollapseIf>
          {/* Auto-Conclude Confirmation Modal */}
          <Modal opened={acConfirmModalOpen} onClose={() => { setAcConfirmModalOpen(false); setAcConfirmText(''); }} title="Enable Auto-Conclude" size="md" centered zIndex={1000}>
            <Text size="sm" mb={15}>Before you enable auto-conclude, please note:</Text>
            <Text size="sm" mb={15} color="dimmed">Once a predicted winner is confirmed and this test is concluded, only the winning version will be shown to all visitors. This action cannot be undone.</Text>
            <Text size="sm" mb={15} color="dimmed">Make sure your Primary Conclude Goal, minimum days, and minimum traffic settings are correct before this test goes live.</Text>
            <Text size="sm" mb={8}>Type <strong>Enable</strong> to confirm:</Text>
            <TextInput size="sm" placeholder="Type Enable" value={acConfirmText} onChange={(e) => setAcConfirmText(e.currentTarget.value)} mb={15} />
            <Group position="right" mt={10}>
              <Button size="sm" variant="outline" onClick={() => { setAcConfirmModalOpen(false); setAcConfirmText(''); }}>Cancel</Button>
              <Button size="sm" color="blue" disabled={acConfirmText.toLowerCase() !== 'enable'} onClick={() => { confirmEnableAutoConclude(); setAcConfirmText(''); }} style={{ cursor: acConfirmText.toLowerCase() === 'enable' ? 'pointer' : 'not-allowed' }}>I Understand — Enable Auto-Conclude</Button>
            </Group>
          </Modal>

          {/* Auto-Conclude Settings Modal */}
          <Modal opened={acSettingsModalOpen} onClose={() => setAcSettingsModalOpen(false)} title="Auto-Conclude" size="lg" centered>
            {acSettings && (
              <>
                {/* Setting 1: Enable Toggle */}
                <Switch
                  size="sm"
                  label="Auto-Conclude"
                  checked={acSettings.auto_conclude_enabled === 1}
                  onChange={(e) => handleToggleAutoConclude(e.currentTarget.checked)}
                  styles={{ track: { cursor: 'pointer' }, label: { cursor: 'pointer' } }}
                  mb={10}
                />
                <Text size={12} color="dimmed" mb={15}>When enabled, Visiopt will automatically conclude this test and set the winner when the Predictive Winner Detection engine reaches high confidence on your selected goal. You will receive a 24-hour warning notification before any conclusion fires.</Text>

                {acSettings.auto_conclude_enabled === 1 && (
                  <>
                    {/* Setting 2: Primary Conclude Goal */}
                    <Text size={13} weight={600} mb={4}>Conclude On</Text>
                    <Select
                      size="xs"
                      placeholder="Select Primary Goal"
                      data={primaryGoalOptions}
                      value={primaryConcludeGoal}
                      disabled={acGoalLocked}
                      onChange={(val) => {
                        if (val) {
                          setPrimaryConcludeGoal(val);
                          saveAutoConcludeSettings({ primaryGoal: val });
                        }
                      }}
                      mb={4}
                      style={{ maxWidth: 350 }}
                    />
                    <Text size={11} color="dimmed" mb={10}>The engine will only trigger auto-conclude when this goal reaches Predicted Winner status. All other goals continue tracking independently.</Text>
                    {acGoalLocked && (
                      <Text size={11} color="orange" mb={10}>The Primary Conclude Goal can only be changed within the first 24 hours after test launch. This prevents mid-test changes that could affect the integrity of the conclusion.</Text>
                    )}

                    {/* Setting 3: Minimum Days */}
                    <Text size={13} weight={600} mb={4}>Minimum Days Running</Text>
                    <NumberInput
                      size="xs"
                      value={acMinDays}
                      min={1}
                      onChange={(val) => {
                        const v = typeof val === 'number' ? val : 7;
                        setAcMinDays(v);
                        saveAutoConcludeSettings({ minDays: v });
                      }}
                      mb={4}
                      style={{ maxWidth: 150 }}
                    />
                    <Text size={11} color="dimmed" mb={4}>Auto-conclude will not fire until the test has been running for at least this many days.</Text>
                    {acMinDays < 7 && (
                      <Text size={11} color="orange" mb={10}>Setting fewer than 7 days may result in an early conclusion. Tests that have not run through a full weekly cycle can be affected by weekday/weekend traffic patterns. We recommend a minimum of 7 days.</Text>
                    )}

                    {/* Setting 4: Minimum Traffic */}
                    <Text size={13} weight={600} mb={4} mt={10}>Minimum Traffic Threshold</Text>
                    <Group spacing="md" mb={4} align="flex-end">
                      <div style={{ maxWidth: 200 }}>
                        <Text size={11} mb={2}>Min Unique Visitors / Variation</Text>
                        <NumberInput
                          size="xs"
                          value={acMinUv}
                          min={100}
                          onChange={(val) => {
                            const v = typeof val === 'number' ? (val < 100 ? 100 : val) : 100;
                            setAcMinUv(v);
                            saveAutoConcludeSettings({ minUv: v });
                          }}
                        />
                      </div>
                      <div style={{ maxWidth: 200 }}>
                        <Text size={11} mb={2}>Min Conversions / Variation</Text>
                        <NumberInput
                          size="xs"
                          value={acMinConv}
                          min={10}
                          onChange={(val) => {
                            const v = typeof val === 'number' ? (val < 10 ? 10 : val) : 10;
                            setAcMinConv(v);
                            saveAutoConcludeSettings({ minConv: v });
                          }}
                        />
                      </div>
                    </Group>
                    <Text size={11} color="dimmed" mb={4}>Auto-conclude will not fire until every active variation has reached these minimums. You can increase these thresholds — you cannot decrease below the engine minimums.</Text>
                    <Text size={11} color="dimmed" mb={10}>For high-value tests we recommend at least 500 unique visitors and 25 conversions per variation before any auto-conclusion fires.</Text>

                    {/* Setting 5: Notification Channels */}
                    <Text size={13} weight={600} mb={4}>Warning Notification — 24 Hours Before Auto-Conclude</Text>
                    <Text size={11} color="dimmed" mb={6}>You will always receive a notification 24 hours before auto-conclude fires. This gives you time to review and cancel if needed. To cancel, turn off auto-conclude before the 24-hour window expires.</Text>
                    <Group spacing="md" mb={10}>
                      <Checkbox
                        size="xs"
                        label="Email"
                        checked={acNotifyEmail}
                        onChange={(e) => {
                          setAcNotifyEmail(e.currentTarget.checked);
                          saveAutoConcludeSettings({ notifyEmail: e.currentTarget.checked });
                        }}
                      />
                      <Checkbox
                        size="xs"
                        label="SMS / Text"
                        checked={acNotifySms}
                        onChange={(e) => {
                          setAcNotifySms(e.currentTarget.checked);
                          saveAutoConcludeSettings({ notifySms: e.currentTarget.checked });
                        }}
                      />
                    </Group>
                  </>
                )}
              </>
            )}
          </Modal>

          {/* Grouped-goal helper line with bulk expand/collapse */}
          {allGoalsMode === 1 && (
            <Group spacing={8} mt={5} mb={-8}>
              <Text size="xs" color="dimmed">
                Showing all {allGoalLabel(conversionTypeShow).replace(/^All\s+/i, '').toLowerCase()} under each variation
              </Text>
              <Text size="xs" color="dimmed">·</Text>
              <Anchor size="xs" onClick={expandAllVariations}>Expand all</Anchor>
              <Text size="xs" color="dimmed">|</Text>
              <Anchor size="xs" onClick={collapseAllVariations}>Collapse all</Anchor>
            </Group>
          )}
  		    <Group>
            {testVariationsData.length > 1 ? (
            <>
  			  	<ScrollIfNarrow>
  			  	<Table highlightOnHover mt={15} mb={2} fontSize="xs" sx={newTableSx}>
             <thead>
               <tr>
                 <th {...stickyTh(0, 56)}>ID</th>
                 <th {...stickyTh(56, 88, { textAlign: 'center' })}>P Status</th>
                 <th {...stickyTh(144, 200)}>Variations</th>
                  {selectedMetrics.includes('Uniques') && <th>Uniques</th>}
                  {selectedMetrics.includes('Visits') && <th>Visits</th>}
                  {selectedMetrics.includes('Conv. # to page') && <th>Conversions</th>}
                  {selectedMetrics.includes('Conv. % to page') && <th>Conv. rate</th>}
                  {selectedMetrics.includes('Percentage Improvement') &&<th>Percentage Improvement</th>}
                 <th>Choose Winner</th>
                 {selectedMetrics.includes('Statistical Significance') &&<th>Statistical Significance <Popover width={420} position="bottom-end" shadow="md" withArrow withinPortal>
                   <Popover.Target><IconInfoCircle size="0.85rem" color="#868e96" style={{ marginBottom: -2, cursor: 'pointer' }} /></Popover.Target>
                   <Popover.Dropdown>
                     <Text size="sm" fw={600} mb={8}>Statistical Significance vs Predictive Winner Detection™</Text>
                     <Text size="xs" color="dimmed" mb={8}>Statistical Significance measures whether the difference between variations is unlikely to be random. It uses a single threshold, typically 95%, and tells you nothing about whether the signal is stable, whether lift is bounded above zero, or whether conversion timing may be affecting the result.</Text>
                     <Text size="xs" color="dimmed" mb={8}>Predictive Winner Detection™ evaluates multiple independent signals simultaneously before making a recommendation. It can surface a winner earlier than statistical significance because it does not rely on one number alone. It can also hold back a recommendation that SS would approve, because one or more signals flagged a concern.</Text>
                     <Text size="xs" color="dimmed">When the two disagree, the Predictive Engine is the more complete picture. Statistical Significance is provided for reference.</Text>
                   </Popover.Dropdown>
                 </Popover></th>}
                 {selectedMetrics.includes('Subscription Revenue') && <th>Subscription Revenue</th>}
                 {webhookSetShow !== 0 && (
                 <>
                  {selectedMetrics.includes('Total Revenue') && <th>Total Revenue</th>}
                  {selectedMetrics.includes('AOV') && <th>AOV</th>}
                  {selectedMetrics.includes('EPC') && <th>EPC</th>}
                 </>
                 )}
                 {selectedMetrics.includes('Avg. Time Spent') && <th>Avg. Time Spent</th>}
                 {hidePauseShow !== 'hide' && (
                 <>
                  <th>Pause Variation</th>
                 </>
                 )}
               </tr>
             </thead>
             <tbody>{testVariationsRows}</tbody>
  	  	    </Table>
  	  	    </ScrollIfNarrow>
            {showNotice && (
              <Group position="left" style={{ marginTop: '-15px' }}>
                <Alert
                  icon={<IconInfoCircle size="1rem" />}
                  color="blue"
                  w="100%"
                >
                  {noticeAutoAdjust
                    ? 'Traffic is balanced hourly based on your configured allocation. Short-term differences in uniques are normal between balancing cycles.'
                    : 'Traffic follows your configured split.'}
                </Alert>
              </Group>
            )}
            {/* Phase 5 — Persistent P Status legend (spec Section 4.2) */}
            <Box style={{ width: '100%' }}>
              <Group spacing="lg" mt={2} mb={6}>
                {P_STATUS_LEGEND.map(([icon, label]) => (
                  <Group key={label} spacing={6}>
                    {pStatusDot(icon)}
                    <Text size="xs" color="dimmed">{label}</Text>
                  </Group>
                ))}
              </Group>
            </Box>
            </>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: "30px", marginBottom: "30px", width: '100%' }}>
                <Text fz={14}>There is no data yet. Please check back in later.</Text>
              </div>
            )}
  		    </Group>
    </>
  );
  const headerCardBody = (
    <>
      {/* Row 1 - title alone, then row 2 - controls. That rhythm is most of
          what makes the two pages read as the same product. */}
      {hdrTitle}
      {/* space-between with the controls in their own wrapping group keeps the
          icon cluster pinned to the right END of the control row. With all of
          them in one wrap container the icons dropped onto a line of their own
          and left a dead gap under the controls. */}
      <Flex align="flex-end" justify="space-between" gap="md" mt="sm" wrap="nowrap">
        <Flex gap="md" align="flex-end" wrap="wrap">
          <Box>
            <Text size="xs" color="dimmed" mb={4}>
              Conversions to{' '}
              <IconHelp
                ref={conversionToRef}
                size="0.9rem"
                color="red"
                style={{ marginBottom: -2, cursor: 'pointer' }}
                onClick={() => handleHelpClick(287)}
              />
            </Text>
            {hdrConvSelect}
          </Box>
          <Box>
            {/* Same small grey label treatment, in place of the clock icon. */}
            <Text size="xs" color="dimmed" mb={4}>Date Range</Text>
            {hdrDatePicker}
          </Box>
          {hdrMetrics}
          {hdrTestMode}
        </Flex>
        {/* The slot Aggregated gives Update View. */}
        <Flex align="center" sx={{ flexShrink: 0 }}>{hdrIcons}</Flex>
      </Flex>
      {hdrBehavioral}
    </>
  );
	return (
	  <>
    {/* Initial load runs in 3 stages: full-page loader until the conversions
        dropdown resolves, then the skeleton until the first report loads, then
        the real data. After that the full-page loader behaves normally. */}
    <VisiLoader VisiLoaderVisible={(pageContentLoading && !conversionsDropdownLoaded) || (SystemUsageLoader && !pageContentLoading)} loaderType="tests" />
	  <HeaderMenu HeaderMenuValue={selectedMemberWebsite} HeaderMenuOnChange={handleHeaderMenuComponentChange}/>
    {/* Portalled to body, matching the Aggregated Report. Rendered inline it
        was positioned against whichever ancestor happened to be positioned -
        SimpleBar's scrolling content - so it landed above the icon. On body the
        coordinates below are simply page coordinates. */}
    {showHelpModal && ReactDOM.createPortal(
      <div>
        {Object.keys(tooltipPosition).map((key) => (
          <SelectTooltip
            key={key}
            isVisible={showHelpModal}
            setIsVisible={setShowHelpModal}
            id={helpTargetId}
            tooltipPosition={tooltipPosition[key]}
          />
        ))}
      </div>,
      document.body,
    )}
	  {/*Audience Size Model*/}
    <AudienceSize
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      isAudienceSizeModalOpen={isAudienceSizeModalOpen}
      onAudienceSizeClose={handleAudienceSizeClose}
      TestOptimizeReport={handleTestOptimizeReport}
      setMarksalestime={setMarksalestime}
    />
    {/*Behavioral Filters Model*/}
    <BehavioralFilter
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      isBehavioralFilterModalOpen={behavioralFilterModalOpen}
      onBehavioralFilterClose={handleBehavioralFilterCloseModal}
      TestOptimizeReport={handleTestOptimizeReport}
      setMarksalestime={setMarksalestime}
      getnetworkhierarchicaldata={handlenetworkhierarchical}
      getbehavioraldimensionfilterdata={handlebehavioraldimensionfilter}
      BehavioralFilterArrCount={setBehavioralFilterArrCount}
    />
    {/*Attribution Model*/}
    <Attribution
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      isAttributionModalOpen={isAttributionModalOpen}
      onAttributionClose={handleAttributionCloseModal}
      TestOptimizeReport={handleTestOptimizeReport}
      setMarksalestime={setMarksalestime}
    />
    {/*Attribution Window*/}
    <AttributionWindow
      pageId={pageId}
      isAttributionWindowModalOpen={isAttributionWindowModalOpen}
      onAttributionWindowClose={handleAttributionWindowCloseModal}
      TestOptimizeReport={handleTestOptimizeReport}
      setMarksalestime={setMarksalestime}
    />
    {/*Traffic Control Model*/}
    <TrafficControl
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      isTrafficControlModalOpen={isTrafficControlModalOpen}
      onTrafficControlClose={handleTrafficControlCloseModal}
    />
    {/*Google Analytics Integration Model*/}
    <GoogleAnalytics
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      taguchiTestId={taguchiTestId}
      isGoogleAnalyticsModalOpen={isGoogleAnalyticsModalOpen}
      onGoogleAnalyticsClose={handleGoogleAnalyticsCloseModal}
    />
    {/*Conversion Logs Model*/}
    <Logs
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      taguchiTestId={taguchiTestId}
      funnelId={testFunnelId}
      testStartDate={testStartDate}
      isConversionLogsModalOpen={isConversionLogsModalOpen}
      onConversionLogsClose={handleConversionLogsCloseModal}
      TestOptimizeReport={handleTestOptimizeReport}
    />
    {/*Test Notes Model*/}
    <TestNotes
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      taguchiTestId={taguchiTestId}
      isTestNotesModalOpen={isTestNotesModalOpen}
      onTestNotesClose={handleTestNotesCloseModal}
    />
    {/*Universal Smart Code Model*/}
    <UniversalSmartCodeModal
      opened={isVisiSmartCodeModalOpen}
      onClose={handleVisiSmartCodeCloseModal}
      websiteId={selectedMemberWebsite}
      websiteUrl={(websiteListData || []).find((w:any) => String(w.website_id) === String(selectedMemberWebsite))?.website_url || ''}
      subdomainVisiSmartCode={false}
      visiSmartCodeSubdomainId={''}
      showAdvancedTracking={false}
      pageId={pageId}
    />
    {/*Test Mode*/}
    <TestMode
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      taguchiTestId={taguchiTestId}
      testModeShow={testModeShow}
      isTestMode={isTestMode}
      TestOptimizeReport={handleTestOptimizeReport}
      setMarksalestime={setMarksalestime}
      systemUsageLoader={setSystemUsageLoader}
      setTestModeName={setTestModeName}
    />
    {/*Set Winner Model*/}
    <SetWinner
      selectedMemberWebsite={selectedMemberWebsite}
      pageId={pageId}
      taguchiTestId={taguchiTestId}
      varIdshow={varIdshow}
      pageUrlShow={pageUrlShow}
      testTypeShow={testTypeShow}
      openWinnerModal={setWinnerModalOpen}
      setWinnerModalOpen={setSetWinnerModalOpen}
      setWinnerCloseModal={handleSetWinnerCloseModal}
      systemUsageLoader={setSystemUsageLoader}
      setloading={setLoading}
    />
    {/*PREDICTIVE STATUS LOG*/}
    <PredictiveStatusLog
      opened={predictiveStatusLogOpen}
      onClose={() => setPredictiveStatusLogOpen(false)}
      pageId={pageId}
      selectedMemberWebsite={selectedMemberWebsite}
      goalType={conversionTypeShow}
      goalId={conversionTypeValueSelected}
    />
    {/*TARGET TEST BY DIMENSIONS*/}
    <TargetTestbyDimensions
      selectedMemberWebsite={selectedMemberWebsite}
      targetTestDimensionModelOpen={isTargetTestByDimensionModelOpen}
      handleTargetDimensionCloseModal={handleTargetTestModelClose}
      pageid={pageId}
      taguchi_type_id={tagichiTypeIdShow}
    />
	  <Container size="xl" py="md" fluid={tableExpanded}>
      {/* Keep the real content mounted (just hidden) while the skeleton shows,
          so child components like SelectTimePicker run their initial fetches
          up front — the page then renders once, with no second loader pass. */}
      {pageContentLoading && <ReviewingTestSkeleton />}
      <div style={{ display: pageContentLoading ? 'none' : 'block' }}>
      <Skeleton visible={loading}>
      {/* Header card: what you are looking at and how it is scoped. */}
      <Card shadow="sm" radius="md" withBorder {...layoutCardProps}>
        {headerCardBody}
      </Card>
      <div style={{ display: variationsBtn === '1' ? "block" : "none" }}>
      {/* Data card: the variations tab, prediction and table. */}
      <Card shadow="sm" radius="md" withBorder mt="md" {...layoutCardProps}>
        {variationsBtn === '1' && impactSummary}
        {variationsBtn === '1' && tabRow}
        {variationsCardBody}
      </Card>
  			<Card shadow="sm" radius="md" withBorder mt="md" mb="md" {...layoutCardProps}>
  				<SectionHeader
            icon={<IconTarget size="1rem" color="#ef4444" />}
            label="Conversion to Page/Goals"
            rightNode={
              <>
                {/* stopPropagation so opening help does not also collapse the
                    section - the whole header row is the toggle now. */}
                <IconHelp
                  ref={conversionToPageRef}
                  size="1.1rem"
                  color="red"
                  style={{ marginBottom: -3, cursor: 'pointer' }}
                  onClick={(event: any) => { event.stopPropagation(); handleHelpClick(28); }}
                />
                {convtopageNameShow ? (
                  <Badge color="blue" variant="outline" size="sm" sx={{ textTransform: 'none' }}>
                    {convtopageNameShow}
                  </Badge>
                ) : null}
              </>
            }
            opened={conversionOpen}
            toggle={() => setConversionOpen((o) => !o)}
          />
        <CollapseIf in={conversionOpen} transitionDuration={0}>
          {/* Select Graph applies in both modes. With an "All" option selected the
              chosen graph is drawn once per goal instead of once for the test. */}
          <Group>
            <Text size="xs" color="dimmed">Select Graph:</Text>
            <Select
              mb={5}
              mt={5}
              dropdownPosition="bottom"
              defaultValue="0"
              maxDropdownHeight={200}
              searchable
              data={[
                { value: '0', label: 'Main Conversions' },
                { value: '1', label: 'Cumulative Conversions' },
                ...(webhookSetShow !== 0 ? [{ value: '2', label: 'Avg. Revenue per Unique' }] : []),
              ]}
              onChange={(value) => setSelectedGraph(value)}
            />
          </Group>
  		    <Grid mb={15}>
  			    <Grid.Col span={9} style={{ textAlign: 'left' }}>
            {allGoalsMode === 1 ? (
              <>
              {/* Conversions / Conversion % drives the facets too, and belongs to
                  Main Conversions exactly as it does for a single goal. */}
              {selectedGraph === '0' && (
              <Chip.Group value={selectedValue} onChange={handleConversionRadioChange}>
                <Flex gap="xs" wrap="wrap" mt="md" mb={10}>
                  <Chip value="1" color="teal" variant="outline">Conversions</Chip>
                  <Chip value="2" color="teal" variant="outline">Conversion %</Chip>
                </Flex>
              </Chip.Group>
              )}
              <GoalFacetCharts
                breakdown={goalBreakdown}
                breakdownChart={goalBreakdownChart}
                goals={allGoalsList}
                graph={selectedGraph}
                metric={selectedValue}
                isDark={isDark}
                showEmpty={showEmptyGoals}
                onToggleEmpty={setShowEmptyGoals}
              />
              </>
            ) : selectedGraph === '2' ? (
              SystemUsageLoader ? (
                <ArpuChartSkeleton isDark={isDark} />
              ) : dataArpuChartRows.length > 1 ? (
                <Chart
                  key={`arpu-${conversionOpen}`}
                  chartType="ColumnChart"
                  width="100%"
                  height="400px"
                  data={dataArpuChartRows}
                  options={optionsArpuChart}
                  loader={<ArpuChartSkeleton isDark={isDark} />}
                />
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                  <Text fz={14}>No revenue data available yet.</Text>
                </div>
              )
            ) : selectedGraph === '1' ? (
            <div>
              <Chart
                key={`line-${conversionOpen}`}
                chartType="LineChart"
                width="100%"
                height="400px"
                data={dataLineChartRows}
                options={optionsLineChart}
              />
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0px' }}>
                  {paginatedLabels.map((label, index) => (
                    <div key={label} style={{ marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          width: '15px',
                          height: '15px',
                          backgroundColor: paginatedColors[index],
                          marginRight: '5px',
                        }}
                      >
                      </div>
                      <span>{label}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '5px' }}>
                    <span onClick={() => setCurrentPage(Math.max(currentPage - 1, 0))} style={{ cursor: currentPage === 0 ? 'none' : 'pointer', opacity: currentPage === 0 ? 0.5 : 1 }}>
                        <IconChevronLeft stroke={2} size="1.125rem"/>
                    </span>
                    <span style={{ margin: '-3px 5px' }}>{currentPage + 1}/{charttotalPages}</span>
                    <span onClick={() => setCurrentPage(Math.min(currentPage + 1, charttotalPages - 1))} style={{ cursor: currentPage === charttotalPages - 1 ? 'none' : 'pointer', opacity: currentPage === charttotalPages - 1 ? 0.5 : 1 }}>
                        <IconChevronRight stroke={2} size="1.125rem"/>
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <>
              <Chip.Group value={selectedValue} onChange={handleConversionRadioChange}>
                <Flex gap="xs" wrap="wrap" mt="md" mb={10}>
                  <Chip value="1" color="teal" variant="outline">Conversions</Chip>
                  <Chip value="2" color="teal" variant="outline">Conversion %</Chip>
                </Flex>
              </Chip.Group>
              {dataColumnChartRows.length > 1 && dataColumnPercentageChartRows.length > 1 && (
                <Chart
                  key={`conversion-${conversionOpen}`}
                  chartType="ColumnChart"
                  width="100%"
                  height="400px"
                  data={selectedValue === "1" ? dataColumnChartRows : dataColumnPercentageChartRows}
                  options={selectedValue === "1" ? optionsColumnChart : optionsColumnPercentageChart}
                />
              )}
              </>
            )}
  			    </Grid.Col>
  			    <Grid.Col span={3} style={{ textAlign: 'left' }} mt={20}>
  				    <Box
  				      sx={(theme) => ({
  				        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : "#f8f9fa",
  				        padding: "10px",
  				        borderRadius: "4px",
  				        width:"auto"
  				      })}
  				    >
  				      {/*<div style={{ display: 'flex'}}>
  				      	<Text size="sm" fz={14} fw={500} mr={5}>Test Start on:</Text>
  				      	<Text size="sm" fz={14}>12-14-2023</Text>
  				      </div>*/}
                <div style={{ display: 'flex'}}>
                  <Text size="sm" fz={14} fw={500} mr={5} style={{ display: 'inline-block',width:"90px",minWidth: 75}}>Test Totals</Text>
                  <div style={{ display: 'inline-block' }}>
                    <Text size="sm" fz={14} mr={5} span>
                      ({testNameShow} -{" "}
                      <Anchor
                        href={testPageLinkShow}
                        target="_blank"
                        fw={500}
                        style={{ overflowWrap: "anywhere" }}
                      >
                        {testPageTitleShow}
                      </Anchor>
                      )
                    </Text>
                  </div>
                </div>
  				      <Space h="xs" />
  			        <div style={{ display: 'flex'}}>
  			        	<Text size="sm" fz={14} fw={500} mr={5}>Uniques:</Text>
  			        	<Text size="sm" fz={14}>{totalUniquesShow}</Text>
  			        </div>
  							<div style={{ display: 'flex'}}>
  								<Text size="sm" fz={14} fw={500} mr={5}>Visits:</Text>
  								<Text size="sm" fz={14}>{totalVisitsShow}</Text>
  							</div>
  							<div style={{ display: 'flex'}}>
  								<Text size="sm" fz={14} fw={500} mr={5}>Conversions:</Text>
  								{/* no single total while several goals are all-goalsd — they are not summed */}
  								<Text size="sm" fz={14}>{allGoalsMode === 1 ? '—' : totalConversionsShow}</Text>
  							</div>
  				    </Box>
  			    </Grid.Col>
  			  </Grid>
          </CollapseIf>
  			</Card>
        {pageBottomMsgShow !== '' && (
        <Card shadow="sm" radius="md" withBorder mt="md" mb="md" {...layoutCardProps}>
          <Grid mb={15}>
            <Text mt={15} fz={14} ml={10}>
              {pageBottomMsgShow}
            </Text>
          </Grid>
        </Card>
        )}
      </div>
      <div style={{ display: variablesBtn === '1' ? "block" : "none" }}>
        <Variables 
          pageId={pageId}
          showVariablesTab={showVariablesTab}
          selectedTimePicker={selectedTimePicker}
          selectedTimePickerFromDate={selectedTimePickerFromDate}
          selectedTimePickerToDate={selectedTimePickerToDate}
          systemUsageLoader={setSystemUsageLoader}
          tabRow={variablesBtn === '1' ? <>{impactSummary}{tabRow}</> : null}
          testName={testsName || testPageTitleShow}
          goalLabel={convtopageNameShow}
        />
      </div>
      {/*ResetStats Modal*/}
      <ResetStats
        selectedMemberWebsite={selectedMemberWebsite}
        pageIdShow={pageIdShow}
        taguchiTestId={taguchiTestId}
        isResetStatsModalOpen={isResetStatsModalOpen}
        onResetStatsClose={handleResetStatsCloseModal}
        TestOptimizeReport={handleTestOptimizeReport}
        setMarksalestime={setMarksalestime}
      />
      {/*Customer Journey Modal*/}
      <CustomerJourney
        emailValue={customerJourneyEmail}
        setEmailValue={setCustomerJourneyEmail}
        isCustomerJourneyModalOpen={isCustomerJourneyModalOpen}
        onCustomerJourneyClose={handleCustomerJourneyClose}
        testJourneyPayload={customerJourneyPayload}
      />
      </Skeleton>
      </div>
	  </Container>
	  </>
	);
}